import React, { Suspense, useMemo, useState, useEffect, useRef, useCallback, useId } from "react";
import { Routes, Route, Link, Navigate, matchPath, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowBigUp,
  ArrowBigDown,
  MessageSquare,
  Atom,
  Link as LinkIcon,
  Quote,
  Wand2,
  Plus,
  Clock,
  Star,
  TrendingUp,
  Search,
  Tag,
  Menu,
  Send,
  BookOpen,
  Video,
  LogIn,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Check,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Copy,
  Trash2,
  Bot,
  Sparkles,
  FileText,
  Link2,
  MoreHorizontal,
  Eye,
  EyeOff,
  Edit2,
  Target,
  Zap,
  Heart,
  KeyRound,
  ShieldCheck,
  UserRound,
  Layers,
} from "lucide-react";
import { motion, AnimatePresence, number, useReducedMotion } from "motion/react";

// shadcn/ui
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "./components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "./components/ui/sheet";
import { Skeleton } from "./components/ui/skeleton";
import { Toaster } from "./components/ui/sonner";
import { ScrollArea } from "./components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { cn } from "./lib/utils"; // your className utility

import { GoogleOAuthProvider, useGoogleLogin, CredentialResponse } from "@react-oauth/google";
import { DeleteAccountDialog } from "./components/auth/DeleteAccountDialog";
import { GoogleSignInButton } from "./components/auth/GoogleSignInButton";
import { MobileTabBar } from "./components/layout/MobileTabBar";
import { initNativeShell, isNativeApp, NATIVE_BACK_EVENT, NATIVE_GO_HOME_EVENT, onAppResume } from "./lib/native";
import { isOnline, onReconnect } from "./lib/network";
import { initPushNotifications, NOTIFICATIONS_CHANGED_EVENT, PUSH_OPEN_PATH_EVENT, type PushOpenPathDetail } from "./lib/push";


import ReactGA from "react-ga4";

import { simulateDelay } from "./lib/async-utils";
import { showSuccessToast, showInfoToast, showErrorToast } from "./lib/toast";
import { validateEmail, validatePassword, validateUsername, validateUrl } from "./lib/validation";
import { CLIENT_ID, API_BASE_URL, GA_MEASUREMENT_ID, ITEMS_PER_PAGE, PUBLIC_SITE_URL } from "./constants/app";
import { Capacitor } from "@capacitor/core";
import { Header, InspireStackLogo } from "./components/layout/Header";
import { WelcomeHero } from "./components/home/WelcomeHero";
import { CategorySidebar as Sidebar, MobileCategorySidebar } from "./components/layout/CategorySidebar";
import { TranslateQuotesButton } from "./components/feed/TranslateQuotesButton";
import { CATEGORIES, categoryIdFromParam, categoryNameFromId, categoryParamFromId } from "./lib/categories";
import { parsePageParam, useUrlLanguage } from "./lib/url-state";
import { ShareLinkButton } from "./components/layout/ShareLinkButton";
import { ThemeProvider, useTheme } from "./components/layout/theme-context";

import { TYPE_META, AI_PROMPT_COMPLEXITY_OPTIONS, AiPromptComplexity, normalizeAiPromptComplexity } from "./constants/feed-meta";
import ApiService from "./services/api-service";
import { FeedItem, Vote, Comment } from "./types/feed";
import { CurrentUser } from "./types/user";
import { formatNumber, normalizePointsCount } from "./lib/format";
import PostDetailPage from "./components/post/PostDetailPage";
// Listen feature disabled - uncomment these imports and the marked JSX below to re-enable.
// import { ListenApp } from "./components/listen/ListenApp";
// import { GlobalPlayerBar } from "./components/listen/GlobalPlayerBar";
// import { PodcastPlayerProvider } from "./lib/podcast-player-context";
// import { AdminListenDashboard } from "./components/admin/listen/AdminListenDashboard";
import { LikedPage } from "./components/liked/LikedPage";
import { UserProfilePage } from "./components/profile/UserProfilePage";
import { NotificationSettingsPage } from "./components/notifications/NotificationSettingsPage";
import { AuthorQuotesPage } from "./components/quotes/AuthorQuotesPage";
import { AuthorCloudPage } from "./components/quotes/AuthorCloudPage";
import { TagCloudPage } from "./components/tags/TagCloudPage";
import { LegalPage } from "./components/legal/LegalPage";
import { AiPromptsPage } from "./components/ai-prompts/AiPromptsPage";
import { AiTopicsPage } from "./components/ai-topics/AiTopicsPage";
import { AiTopicDetailPage } from "./components/ai-topics/AiTopicDetailPage";
import { TagPage } from "./components/tags/TagPage";


const FeedList = React.lazy(() => import("./components/feed/FeedList"));
let hasInitializedAnalytics = false;
type FeedSortOption = "trending" | "recent" | "liked";
type FeedSortState = FeedSortOption | "custom";

// Label/icon for each sort/filter mode, matching what the dropdown offers.
const FEED_SORT_META: Record<FeedSortOption, { label: string; Icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = {
  trending: { label: "Trending", Icon: TrendingUp },
  recent: { label: "Recent", Icon: Clock },
  liked: { label: "Liked", Icon: Heart },
};
const DEFAULT_PAGE_TITLE = "The Deepam - Grow to be Great";

// ?type= accepts the TYPE_META keys ("quote", "prompt", ...), plurals
// ("quotes") and the backend's "aiprompt"; internally prompts are "aiprompt".
function feedTypeFromParam(param: string | null): string {
  const value = (param || "").trim().toLowerCase();
  if (!value) return "all";
  const key = value in TYPE_META ? value : value.replace(/s$/, "");
  if (key === "prompt" || key === "aiprompt") return "aiprompt";
  return key in TYPE_META ? key : "all";
}

function feedTypeToParam(type: string): string | null {
  if (!type || type === "all") return null;
  return type === "aiprompt" ? "prompt" : type;
}

function feedTypeLabel(type: string): string | null {
  const key = type === "aiprompt" ? "prompt" : type;
  return TYPE_META[key as keyof typeof TYPE_META]?.label ?? null;
}

function feedSortFromParam(param: string | null): FeedSortOption {
  return param === "recent" || param === "liked" ? param : "trending";
}

// Builds the canonical URL for a feed view. Sort (recent/liked) applies on
// top of whichever category/type filter is active, so e.g. ?type=article
// and ?type=article&sort=liked are both valid, filtered views.
function buildFeedUrl({
  category = "all",
  type = "all",
  sort = "trending",
  page = 1,
  lang,
}: {
  category?: string | number;
  type?: string;
  sort?: FeedSortState;
  page?: number;
  // Quote language (?lang=); omitted when undefined.
  lang?: string;
}): string {
  const categoryParam = categoryParamFromId(category);
  const typeParam = feedTypeToParam(type);
  const params = new URLSearchParams();
  if (typeParam) params.set("type", typeParam);
  if (sort === "recent" || sort === "liked") params.set("sort", sort);
  if (page > 1) params.set("page", String(page));
  if (lang) params.set("lang", lang);
  const path = categoryParam ? `/category/${encodeURIComponent(categoryParam)}` : "/";
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

const FIRST_VISIT_KEY = "inspirelens_first_visit_at";
const LAST_VISIT_KEY = "inspirelens_last_visit_at";
const SESSION_VISIT_KEY = "inspirelens_session_visit_tracked";
const KNOWN_USER_KEY_PREFIX = "inspirelens_user_known_";

const trackAnalyticsEvent = (eventName: string, params: Record<string, any> = {}) => {
  if (!GA_MEASUREMENT_ID) return;
  try {
    ReactGA.event(eventName, params);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`Analytics event "${eventName}" failed to dispatch`, error);
    }
  }
};

const identifyAnalyticsUser = (user: Partial<CurrentUser> | null | undefined) => {
  if (!GA_MEASUREMENT_ID || !user?.id) return;

  ReactGA.set({ user_id: String(user.id) });

  const gaInstance = ReactGA as unknown as { gtag?: (...args: any[]) => void };
  gaInstance.gtag?.("set", "user_properties", {
    username: user.username || undefined,
    email: user.email || undefined,
    mode_preference: user.mode || undefined,
  });
};

const resolveUserEngagementStatus = (userId?: number | string | null) => {
  if (!userId) return "anonymous";
  const key = `${KNOWN_USER_KEY_PREFIX}${userId}`;
  const hasBeenSeen = localStorage.getItem(key) === "true";
  if (!hasBeenSeen) {
    localStorage.setItem(key, "true");
    return "new";
  }
  return "returning";
};

const AUTO_CLOSE_SECONDS = 15;

const useGoogleAnalytics = () => {
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) {
      if (import.meta.env.DEV) {
        console.warn("Google Analytics measurement ID is missing.");
      }
      return;
    }

    if (!hasInitializedAnalytics) {
      ReactGA.initialize(GA_MEASUREMENT_ID);
      hasInitializedAnalytics = true;
    }

    if (typeof window === "undefined") return;

    const trackPageView = () => {
      const page = window.location.pathname + window.location.search;
      if (isNativeApp) {
        // Otherwise every app hit is reported as https://localhost/...
        ReactGA.set({ page_location: `${PUBLIC_SITE_URL}${page}`, app_platform: Capacitor.getPlatform() });
      }
      ReactGA.send({ hitType: "pageview", page });
    };

    const trackVisitAttributes = () => {
      // Track unique vs returning visitors each browser session
      const sessionTracked = sessionStorage.getItem(SESSION_VISIT_KEY);
      if (sessionTracked) {
        return;
      }

      const firstVisit = localStorage.getItem(FIRST_VISIT_KEY);
      const hasVisitedBefore = Boolean(firstVisit);
      const visitStatus = hasVisitedBefore ? "returning" : "new";

      trackAnalyticsEvent("user_visit", {
        status: visitStatus,
        first_visit_at: firstVisit || undefined,
      });

      if (hasVisitedBefore) {
        trackAnalyticsEvent("repeat_visitor", {
          last_visit_at: localStorage.getItem(LAST_VISIT_KEY) || undefined,
        });
      }

      const nowIso = new Date().toISOString();
      if (!hasVisitedBefore) {
        localStorage.setItem(FIRST_VISIT_KEY, nowIso);
      }
      localStorage.setItem(LAST_VISIT_KEY, nowIso);
      sessionStorage.setItem(SESSION_VISIT_KEY, "1");
    };

    trackPageView();
    trackVisitAttributes();

    window.addEventListener("hashchange", trackPageView);
    window.addEventListener("popstate", trackPageView);

    return () => {
      window.removeEventListener("hashchange", trackPageView);
      window.removeEventListener("popstate", trackPageView);
    };
  }, []);
};

interface UpdateThemeResponse {
  message: string;
  theme: string;
}

function postLenght(postCounts: any) {
  if(postCounts.length === 0) return 0
  const total =
    postCounts[0].article_count +
    postCounts[0].aiprompt_count +
    postCounts[0].book_count +
    postCounts[0].quote_count +
    postCounts[0].video_count;
  return total;
}



// Logo Component

const initialItems: FeedItem[] = await (async () => {
  try {
    const posts = await ApiService.getAllPosts({ sort: "trending" });
    // console.log(posts);
    const totalItems = postLenght(posts.posts);

    const itemsArray: FeedItem[] = [];
    for (let i = 0; i < totalItems; i += 1) {
      const post = posts.posts[i];
      if (!post) continue;

      itemsArray.push({
        id: post.id ?? 0,
        type: post.type,
        title: post.title || post.content || `Item ${i + 1}`,
        content: post.content || "",
        author: post.author || "",
        language: post.language ?? null,
        url: post.url || "",
        category_id: post.category_id || "",
        category: post.category_name || "",
        points: post.points || null,
        points_count: normalizePointsCount(post.points_count),
        tags: post.tags || [],
        tag_meta: post.tag_meta || null,
        folksonomy_tags: post.folksonomy_tags || null,
        views: post.views || 0,
        comments: post.comments || [],
        user: post.username || "",
        user_id: post.user_id ?? undefined,
        createdAt: post.created_at || Date.now(),
        updatedAt: post.updated_at || post.created_at || Date.now(),
        updated_at: post.updated_at || post.created_at || Date.now(),
      });
    }

    return itemsArray;
  } catch (e) {
    console.error('Error fetching initial items:', e);
    return [];
  }
})();


function InspireLensLogo({ size = "md" }: { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10", 
    lg: "w-12 h-12",
    xl: "w-16 h-16"
  };

  return (
    <div className={`${sizeClasses[size]} rounded-xl bg-gradient-to-br from-slate-700 via-slate-600 to-slate-800 dark:from-slate-200 dark:via-slate-300 dark:to-slate-100 grid place-items-center shadow-lg relative overflow-hidden`}>
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent dark:via-black/10"></div>
      <div className="relative flex items-center justify-center">
        <div className="absolute w-3 h-3 bg-white/90 dark:bg-slate-800/90 rounded-full animate-pulse"></div>
        <div className="w-6 h-6 border-2 border-white/80 dark:border-slate-800/80 rounded-full flex items-center justify-center">
          <Atom className="w-3 h-3 text-white/90 dark:text-slate-800/90" />
        </div>
      </div>
    </div>
  );
}
















// Filled, rounded-full "chip" look shared by every content-type/sort filter
// row - swapped in for the old thin-outline buttons, which read as flat and
// bureaucratic (especially as a two-row grid on mobile).
const filterChipClass = (isActive: boolean) =>
  `flex items-center gap-1.5 shrink-0 rounded-full px-3.5 lg:px-2.5 whitespace-nowrap transition-all duration-200 ${
    isActive
      ? "bg-slate-900 text-white shadow-sm shadow-slate-900/20 hover:bg-slate-900 hover:text-white dark:bg-white dark:text-slate-900 dark:hover:bg-white dark:hover:text-slate-900"
      : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-600 dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-300"
  }`;

function ContentTypeFilter({ activeType, onTypeChange }: { activeType: string; onTypeChange: (type: string) => void }) {
  const handleTypeChange = (type: string) => {
    onTypeChange(type);
  };

  return (
    // A single swipeable row on mobile (like a native app's filter chips);
    // from sm up it wraps instead of scrolling, so nothing ever ends up
    // hidden behind a scrollbar. It's sized to its own content (not forced
    // to share width via flex-grow) so it can never be squeezed smaller
    // than it needs and spill, unseen, under the toolbar's right-hand group.
    <div className="flex gap-1 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible xl:flex-nowrap -mx-3 px-3 sm:mx-0 sm:px-0 py-0.5">
      <Button variant="ghost" size="sm" onClick={() => handleTypeChange("all")} className={filterChipClass(activeType === "all")}>
        <Sparkles className="w-3.5 h-3.5" />
        <span className="text-xs">All Types</span>
      </Button>

      {Object.entries(TYPE_META).map(([key, meta], index) => {
        if(key === "prompt"){
          key = "aiprompt"
        }
        const isActive = activeType === key;

        return (
          <Button
            key={`${key}-${index}`}
            variant="ghost"
            size="sm"
            onClick={() => handleTypeChange(key)}
            className={filterChipClass(isActive)}
          >
            {meta.icon}
            <span className="text-xs">{meta.label}</span>
          </Button>
        );
      })}

      <Button asChild variant="ghost" size="sm" className={filterChipClass(false)}>
        <Link to="/ai-topics">
          <Layers className="w-3.5 h-3.5" />
          <span className="text-xs">AI Topics</span>
        </Link>
      </Button>
    </div>
  );
}

// A single dropdown covering Trending/Recent sort plus a jump to the Liked
// page, instead of three separate buttons.
function FeedSortToggle({ value, onChange, disabled = false }: { value: FeedSortState; onChange: (value: FeedSortOption) => void; disabled?: boolean }) {
  const handleValueChange = (next: string) => {
    onChange(next as FeedSortOption);
  };

  const activeOption = value === "custom" ? "trending" : value;
  const ActiveIcon = FEED_SORT_META[activeOption].Icon;

  // The currently-selected list item gets the exact same active-button fill
  // as the trigger/ContentTypeFilter (not just a neutral hover highlight),
  // so the color stays consistent whether the dropdown is open or closed.
  const itemClassName =
    "focus:bg-slate-100 dark:focus:bg-slate-700 data-[state=checked]:bg-slate-800 data-[state=checked]:text-white dark:data-[state=checked]:bg-slate-200 dark:data-[state=checked]:text-slate-900 data-[state=checked]:focus:bg-slate-800 dark:data-[state=checked]:focus:bg-slate-200";

  return (
    <Select value={activeOption} onValueChange={handleValueChange} disabled={disabled}>
      {/* Same "active" color pattern as ContentTypeFilter's selected button
          (e.g. "All Types"): navy fill + white icon/text in light mode,
          which inverts to a white fill + near-black icon/text in dark mode. */}
      <SelectTrigger
        // In the app it's a light pill like the category button beside it, so
        // the only dark fill in the toolbar is the selected type chip.
        className={
          isNativeApp
            ? "h-10 w-auto shrink-0 gap-1.5 rounded-full border-slate-200 bg-white px-3.5 text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            : "h-9 w-[136px] shrink-0 rounded-md bg-slate-800 text-white border-slate-800 transition-all duration-200 hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200 dark:hover:bg-slate-300"
        }
        aria-label="Sort or view liked content"
      >
        {/* SelectValue is given explicit text children so it doesn't mirror
            the selected item's icon+text (which would both duplicate this
            icon and hit the line-clamp/flex stacking bug on mixed content). */}
        {/* Icons need a class containing "text-" (here "text-current"), or
            the shared SelectTrigger/SelectItem styling's
            [&_svg:not([class*='text-'])]:text-muted-foreground rule force-
            colors them gray, overriding the active white/slate-900 color
            they're meant to inherit. */}
        <span className="flex items-center gap-2">
          <ActiveIcon className="size-4 text-current" aria-hidden="true" />
          <SelectValue>{FEED_SORT_META[activeOption].label}</SelectValue>
        </span>
      </SelectTrigger>
      <SelectContent>
        {/* Each item's icon+label is wrapped in its own explicit flex span
            rather than left as bare siblings, since relying on the shared
            SelectItem's implicit "last span" selector to lay them out
            horizontally isn't reliable. */}
        <SelectItem value="trending" className={itemClassName}>
          <span className="flex items-center gap-2">
            <TrendingUp className="size-4 text-current" />
            Trending
          </span>
        </SelectItem>
        <SelectItem value="recent" className={itemClassName}>
          <span className="flex items-center gap-2">
            <Clock className="size-4 text-current" />
            Recent
          </span>
        </SelectItem>
        <SelectItem value="liked" className={itemClassName}>
          <span className="flex items-center gap-2">
            <Heart className="size-4 text-current" />
            Liked
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}


interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
}: PaginationProps): React.ReactElement | null {
  const getPageNumbers = () => {
    const pages: number[] = [];
    const visiblePages = 5;

    let start = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let end = Math.min(totalPages, start + visiblePages - 1);

    if (end - start < visiblePages - 1) {
      start = Math.max(1, end - visiblePages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-1 mt-8 select-none">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || isLoading}
        className="w-8 h-8 rounded border-slate-300 dark:border-slate-600"
      >
        <ChevronLeft className="w-3 h-3" />
      </Button>

      {getPageNumbers().map((page) => (
        <Button
          key={page}
          variant={page === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(page)}
          disabled={isLoading}
          className={`w-8 h-8 rounded transition-all duration-150 ${
            page === currentPage
              ? "bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200"
              : "border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-700"
          }`}
        >
          {page}
        </Button>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || isLoading}
        className="w-8 h-8 rounded border-slate-300 dark:border-slate-600"
      >
        <ChevronRight className="w-3 h-3" />
      </Button>
    </div>
  );
}

function LoginPromptPopup({
  open,
  onClose,
  onLogin,
  autoCloseSecondsLeft,
}: {
  open: boolean;
  onClose: () => void;
  onLogin: () => void;
  autoCloseSecondsLeft: number;
}) {
  const { theme: selectedTheme } = useTheme();
  const resolvedTheme = useMemo(() => {
    if (typeof window === "undefined") return "light";
    if (selectedTheme === "system") {
      return document.documentElement.classList.contains("dark") ? "dark" : "light";
    }
    return selectedTheme;
  }, [selectedTheme]);
  const isDarkMode = resolvedTheme === "dark";
  const progress = Math.max(0, Math.min(autoCloseSecondsLeft / AUTO_CLOSE_SECONDS, 1));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="login-prompt-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
        >
          <motion.div
            className={`absolute inset-0 ${isDarkMode ? "bg-slate-950/40" : "bg-white/60"} backdrop-blur-3xl`}
            style={{
              backdropFilter: "blur(3px)",
              WebkitBackdropFilter: "blur(3px)",
            }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className={`relative max-w-xl w-full rounded-[32px] p-6 sm:p-8 backdrop-blur-2xl shadow-[0_20px_60px_rgba(15,23,42,0.45)] ${
              isDarkMode
                ? "border border-slate-800/70 bg-slate-900/85 text-white"
                : "border border-slate-200/80 bg-white/80 text-slate-900"
            }`}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Log in to The Deepam"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close login prompt"
              className={`absolute top-4 right-4 transition-colors ${isDarkMode ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-lg">
                  {/* <Sparkles className="w-6 h-6" /> */}
                  <InspireStackLogo  width={50} height={50} rounded={false} alt="The Deepam Logo" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-slate-800 ">The Deepam</p>
                  <h3 className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-neutral-400 to-slate-500 dark:text-white">Login / Signup</h3>
                </div>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Dive into curated wisdom, save the stories that matter, and get personalized inspiration every time you visit.
              </p>

              <div className="grid gap-3 sm:grid-cols-3 text-[0.7rem] uppercase tracking-[0.2em] text-slate-500">
                {["Curated", "Community", "Momentum"].map((badge) => (
                  <span
                    key={badge}
                    className={`rounded-full border px-3 py-1 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] ${
                      isDarkMode
                        ? "border-slate-700 bg-slate-900/60 text-slate-200"
                        : "border-slate-200 bg-white/80 text-slate-600"
                    }`}
                  >
                    {badge}
                  </span>
                ))}
              </div>

              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between text-[0.65rem] font-semibold uppercase tracking-[0.4em]">
                  <span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>
                    Auto closing in {Math.max(autoCloseSecondsLeft, 0)}s
                  </span>
                  <span className={isDarkMode ? "text-slate-300" : "text-slate-500"}>
                    {Math.round(progress * 100)}%
                  </span>
                </div>
                <div className={`h-1 w-full rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-300"
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 mt-1">
                <Button
                  onClick={() => {
                    onLogin();
                  }}
                  className={`w-full rounded-2xl transition-colors ${
                    isDarkMode
                      ? "bg-slate-100 text-slate-900 hover:bg-white"
                      : "bg-slate-900 text-white hover:bg-slate-950"
                  }`}
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Continue with Google
                </Button>
                <div className={`text-center text-xs ${isDarkMode ? "text-slate-300" : "text-slate-500"}`}>
                  We never post without your permission. Secure, private, and lightning fast.
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Enhanced Create Content Modal
function CreateContentModal({
  open,
  onClose,
  onSubmit,
  currentUser,
  onRequireAuth,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (item: any) => void;
  currentUser?: { id?: number; username?: string; firstName?: string; name?: string; picture?: string; email?: string } | null;
  onRequireAuth?: () => void;
}) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState<keyof typeof TYPE_META>("prompt");
  const [title, setTitle] = useState("");
  const [quote, setQuote] = useState("");
  const [author, setAuthor] = useState("");
  const [promptDetails, setPromptDetails] = useState("");
  const [complexity, setComplexity] = useState<AiPromptComplexity>("beginner");
  const [url, setUrl] = useState("");
  const [summary, setSummary] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const totalSteps = 3;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
   
   if(category === ""){
      newErrors.category = "Category is required";
    }
    // Type-specific validations
    if (type === "quote") {
      if (!quote.trim()) {
        newErrors.quote = "Quote is required";
      } else if (quote.trim().length < 10) {
        newErrors.quote = "Quote should be at least 10 characters";
      } else if (quote.trim().length > 500) {
        newErrors.quote = "Quote should not exceed 500 characters";
      }
      
      if (!author.trim()) {
        newErrors.author = "Author is required";
      } else if (author.trim().length < 2) {
        newErrors.author = "Author name must be at least 2 characters";
      }
    }

    if (type === "prompt") {
      if (!promptDetails.trim()) {
        newErrors.promptDetails = "AI prompt details are required";
      } else if (promptDetails.trim().length < 20) {
        newErrors.promptDetails = "Prompt should be at least 20 characters";
      } else if (promptDetails.trim().length > 2000) {
        newErrors.promptDetails = "Prompt should not exceed 2000 characters";
      }
    }

    if (type === "article" || type === "video") {
       if (!title.trim()) {
      newErrors.title = "Title is required";
    } else if (title.length < 5) {
      newErrors.title = "Title must be at least 5 characters";
    }
 
      if (!url.trim()) {
        newErrors.url = "URL is required";
      } else if (!validateUrl(url.trim())) {
        newErrors.url = "Please enter a valid URL starting with http:// or https://";
      }
    }

    if (type === "book") {
        if (!title.trim()) {
      newErrors.title = "Title is required";
    } else if (title.length < 5) {
      newErrors.title = "Title must be at least 5 characters";
    }
      if (!author.trim()) {
        newErrors.author = "Author is required";
      } else if (author.trim().length < 2) {
        newErrors.author = "Author name must be at least 2 characters";
      }
      
      const trimmedUrl = url.trim();
      if (!trimmedUrl) {
        newErrors.url = "Book link is required";
      } else if (!validateUrl(trimmedUrl)) {
        newErrors.url = "Please enter a valid URL starting with http:// or https://";
      }
      
      const trimmedSummary = summary.trim();
      if (trimmedSummary) {
        if (trimmedSummary.length < 20) {
          newErrors.summary = "Summary should be at least 20 characters";
        } else if (trimmedSummary.length > 1000) {
          newErrors.summary = "Summary should not exceed 1000 characters";
        }
      }
    }
    
    return newErrors;
  };

  const handleSubmit = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    
    if (!currentUser) {
      onRequireAuth?.();
      return;
    }

    const creatorId = currentUser.id;
    const creatorDisplayName = currentUser.firstName ?? currentUser.name ?? currentUser.username ?? "You";

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    // Simulate a brief delay for better UX
    await simulateDelay(500);

    let content = "";
    let finalAuthor = "";
    
    if (type === "quote") {
      content = quote;
      finalAuthor = author;
    } else if (type === "prompt") {
      content = promptDetails;
    } else if (type === "book") {
      content = summary;
      finalAuthor = author;
    }
    //console.log(title);
    
    const payload = {
      userId: creatorId,
      type,
      title: title.trim(),
      complexity,
      content: content.trim(),
      author: finalAuthor.trim(),
      url: url.trim(),
      category,
      // A leading '#' is just how tags are typed/displayed, not part of the
      // tag itself - strip it and dedupe so "#focus" and "focus" don't end
      // up as two different tags.
      tags: Array.from(
        new Set(
          tags
            .split(",")
            .map((t) => t.trim().toLowerCase().replace(/^#+/, ""))
            .filter(Boolean)
        )
      ),
      summary: summary.trim(), // Added summary property
    };

    try {
      const response = await ApiService.createContent(payload);
      const createdPost = response?.post;

      const parseJsonArray = <T,>(value: any): T[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed;
            if (parsed) return [parsed];
          } catch (err) {
            console.warn("Failed to parse JSON field while creating content:", err);
          }
          return [];
        }
        return [value];
      };

      const ensureCreatorUpvote = (votes: Vote[]): Vote[] => {
        if (!creatorId) return votes;
        if (votes.some((vote) => vote.user_id === creatorId)) {
          return votes;
        }
        const syntheticVote: Vote = {
          id: Date.now(),
          user_id: creatorId,
          username: creatorDisplayName,
          vote_type: "up",
          created_at: new Date().toISOString(),
        };
        return [syntheticVote, ...votes];
      };

      const parsedPoints = ensureCreatorUpvote(parseJsonArray<Vote>(createdPost?.points));
      const parsedComments = parseJsonArray<Comment>(createdPost?.comments);
      const parsedTags = parseJsonArray<string>(createdPost?.tags);

      const createdTimestamp = createdPost?.created_at ?? createdPost?.createdAt ?? new Date().toISOString();
      const updatedTimestamp = createdPost?.updated_at ?? createdPost?.updatedAt ?? createdTimestamp;

      const normalizedPointsCount = Math.max(
        1,
        normalizePointsCount(createdPost?.points_count ?? parsedPoints.length)
      );

      const finalCategoryName = createdPost?.category_name ?? category;

      const newItem: FeedItem = {
        id: Number(createdPost?.id ?? response?.id ?? Date.now()),
        type: createdPost?.type ?? payload.type,
        title: createdPost?.title ?? payload.title,
        content: createdPost?.content ?? payload.content,
        summary: createdPost?.summary ?? payload.summary,
        author: createdPost?.author ?? payload.author,
        url: createdPost?.url ?? payload.url,
        category: createdPost?.category ?? finalCategoryName,
        category_name: finalCategoryName,
        category_id: createdPost?.category_id ?? category,
        tags: parsedTags.length ? parsedTags : payload.tags,
        points: parsedPoints,
        points_count: normalizedPointsCount,
        comments: parsedComments,
        user: createdPost?.user ?? createdPost?.username ?? creatorDisplayName,
        username: createdPost?.username ?? creatorDisplayName,
        picture: currentUser.picture ?? createdPost?.picture,
        complexity: createdPost?.complexity ?? payload.complexity,
        views: createdPost?.views ?? 0,
        created_at: createdTimestamp,
        createdAt: createdTimestamp,
        updated_at: updatedTimestamp,
        updatedAt: updatedTimestamp,
      };

      onSubmit(newItem);

      // Reset form
      setStep(1);
      setTitle("");
      setQuote("");
      setAuthor("");
      setPromptDetails("");
      setComplexity("beginner");
      setUrl("");
      setSummary("");
      setTags("");
      setType("prompt");
      setCategory("");

      showSuccessToast("Content created successfully!");
      onClose();
    } catch (error) {
      console.error("Failed to create content:", error);
      const message = error instanceof Error ? error.message : "Failed to create content. Please try again.";
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form state
      setStep(1);
      setType("prompt");
      setTitle("");
      setQuote("");
      setAuthor("");
      setPromptDetails("");
      setComplexity("beginner");
      setUrl("");
      setSummary("");
      setCategory("");
      setTags("");
      setErrors({});
      onClose();
    }
  };

  const handleNext = () => {
  if (step < totalSteps) {
    if (step === 2) {
      const formErrors = validateForm();

      // If there are errors
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);

        // Allow moving to next step only if category is the only error
        if (formErrors.category && Object.keys(formErrors).length === 1) {
          setStep(step + 1);
        }

        return; // stop execution
      }

      // No errors, proceed
      setStep(step + 1);
    } else {
      // For other steps, move forward directly
      setStep(step + 1);
    }
  }
};


  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Choose Content Type";
      case 2: return "Add Your Content";
      case 3: return "Categorize & Share";
      default: return "Create Content";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return "What type of inspirational content would you like to share?";
      case 2: return "Fill in the details for your content";
      case 3: return "Choose a category and add tags to help others discover your content";
      default: return "";
    }
  };

  const renderTypeSpecificFields = () => {
    switch (type) {
      case "quote":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quote" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Quote Content *
              </Label>
              <Textarea
                id="quote"
                rows={4}
                value={quote}
                onChange={(e) => {
                  setQuote(e.target.value);
                  setTitle(e.target.value); // Sync title with quote
                  if (errors.quote) setErrors(prev => ({ ...prev, quote: null }));
                }}
                placeholder="Enter the inspiring quote..."
                className={`border rounded-lg transition-colors resize-none text-base ${
                  errors.quote ? "border-red-300 focus:border-red-500" : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
                }`}
                disabled={isLoading}
              />
              {errors.quote && (
                <motion.p 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-600 text-sm flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.quote}
                </motion.p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="author" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Author *
              </Label>
              <Input
                id="author"
                value={author}
                onChange={(e) => {
                  setAuthor(e.target.value);
                  if (errors.author) setErrors(prev => ({ ...prev, author: null }));
                }}
                placeholder="Who said this quote?"
                className={`h-12 border rounded-lg transition-colors text-base ${
                  errors.author ? "border-red-300 focus:border-red-500" : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
                }`}
                disabled={isLoading}
              />
              {errors.author && (
                <motion.p 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-600 text-sm flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.author}
                </motion.p>
              )}
            </div>
          </div>
        );

      case "prompt":
        return (
          <div className="space-y-2">
            <Label htmlFor="promptDetails" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              AI Prompt Content *
            </Label>
            <Textarea
              id="promptDetails"
              rows={8}
              value={promptDetails}
              onChange={(e) => {
                setPromptDetails(e.target.value);
                setTitle(e.target.value); // Sync title with prompt (first 60 chars)
                if (errors.promptDetails) setErrors(prev => ({ ...prev, promptDetails: null }));
              }}
              placeholder="Write your AI prompt here. Be specific about the role, context, instructions, and expected output format..."
              className={`border rounded-lg transition-colors resize-none text-base ${
                errors.promptDetails ? "border-red-300 focus:border-red-500" : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
              }`}
              disabled={isLoading}
            />
            {errors.promptDetails && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-600 text-sm flex items-center gap-1"
              >
                <AlertCircle className="w-3 h-3" />
                {errors.promptDetails}
              </motion.p>
            )}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                💡 <strong>Tip:</strong> Write your complete AI prompt here. Include the role, context, specific instructions, and expected output format for best results.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="promptComplexity" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Complexity Level *
              </Label>
              <Select value={complexity} onValueChange={(value: string) => setComplexity(normalizeAiPromptComplexity(value))}>
                <SelectTrigger id="promptComplexity" className="h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROMPT_COMPLEXITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case "article":

     case "video":
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="title" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          Title *
        </Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (errors.title) setErrors(prev => ({ ...prev, title: null }));
          }}
          placeholder="Enter a compelling title that captures attention..."
          className={`h-12 border rounded-lg transition-colors text-base ${
            errors.title
              ? "border-red-300 focus:border-red-500"
              : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
          }`}
          disabled={isLoading}
        />
        {errors.title && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-600 text-sm flex items-center gap-1"
          >
            <AlertCircle className="w-3 h-3" />
            {errors.title}
          </motion.p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="url" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          {type === "article" ? "Article" : "Video"} URL *
        </Label>
        <Input
          id="url"
          type="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            // Sync title with URL (first 60 chars)
            if (errors.url) setErrors(prev => ({ ...prev, url: null }));
          }}
          placeholder={`https://example.com/${type === "article" ? "article" : "watch"}`}
          className={`h-12 border rounded-lg transition-colors text-base ${
            errors.url
              ? "border-red-300 focus:border-red-500"
              : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
          }`}
          disabled={isLoading}
        />
        {errors.url && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-600 text-sm flex items-center gap-1"
          >
            <AlertCircle className="w-3 h-3" />
            {errors.url}
          </motion.p>
        )}
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
          <p className="text-xs text-green-700 dark:text-green-300">
            🔗 Share a link to an inspiring {type} that others will find valuable.
          </p>
        </div>
      </div>
    </>
  );


      case "book":
        return (
          <>
          <div className="space-y-2">
                    <Label htmlFor="title" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Title *
                    </Label>
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                      }}
                      placeholder="Enter a compelling title that captures attention..."
                      className={`h-12 border rounded-lg transition-colors text-base ${
                        errors.title ? "border-red-300 focus:border-red-500" : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
                      }`}
                      disabled={isLoading}
                    />
                    {errors.title && (
                      <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-600 text-sm flex items-center gap-1"
                      >
                        <AlertCircle className="w-3 h-3" />
                        {errors.title}
                      </motion.p>
                    )}
                  </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="author" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Author *
              </Label>
              <Input
                id="author"
                value={author}
                onChange={(e) => {
                  setAuthor(e.target.value);
                  if (errors.author) setErrors(prev => ({ ...prev, author: null }));
                }}
                placeholder="Who wrote this book?"
                className={`h-12 border rounded-lg transition-colors text-base ${
                  errors.author ? "border-red-300 focus:border-red-500" : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
                }`}
                disabled={isLoading}
              />
              {errors.author && (
                <motion.p 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-600 text-sm flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.author}
                </motion.p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="summary" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Summary & Key Insights (optional)
              </Label>
              <Textarea
                id="summary"
                rows={5}
                value={summary}
                onChange={(e) => {
                  setSummary(e.target.value);
                  if (errors.summary) setErrors(prev => ({ ...prev, summary: null }));
                }}
                placeholder="Share the key insights, main takeaways, and why this book is valuable..."
                className={`border rounded-lg transition-colors resize-none text-base ${
                  errors.summary ? "border-red-300 focus:border-red-500" : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
                }`}
                disabled={isLoading}
              />
              {errors.summary && (
                <motion.p 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-600 text-sm flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.summary}
                </motion.p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="url" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Book Link *
              </Label>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (errors.url) setErrors(prev => ({ ...prev, url: null }));
                }}
                placeholder="https://example.com/book-link"
                className={`h-12 border rounded-lg transition-colors text-base ${
                  errors.url ? "border-red-300 focus:border-red-500" : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
                }`}
                disabled={isLoading}
              />
              {errors.url && (
                <motion.p 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-600 text-sm flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.url}
                </motion.p>
              )}
            </div>
          </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden border-0 bg-white dark:bg-slate-800 shadow-xl rounded-xl max-h-[95vh] overflow-y-auto">
        <div className="relative">
          {/* Beautiful Header with Gradient */}
          <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
            <div className="relative">
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <InspireLensLogo size="md" />
                  <div>
                    <DialogTitle className="text-xl font-bold">
                      Share Your Inspiration
                    </DialogTitle>
                    <DialogDescription className="text-slate-300 dark:text-slate-400 text-sm">
                      Create content that inspires and empowers others
                    </DialogDescription>
                  </div>
                </div>
                
                {/* Progress Steps */}
                <div className="flex items-center gap-2 mt-4">
                  {Array.from({ length: totalSteps }, (_, i) => (
                    <div key={i} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                        i + 1 === step 
                          ? "bg-white text-slate-800 shadow-lg" 
                          : i + 1 < step 
                          ? "bg-green-500 text-white"
                          : "bg-slate-600 dark:bg-slate-700 text-slate-300"
                      }`}>
                        {i + 1 < step ? <Check className="w-4 h-4" /> : i + 1}
                      </div>
                      {i < totalSteps - 1 && (
                        <div className={`w-12 h-0.5 mx-2 transition-all duration-300 ${
                          i + 1 < step ? "bg-green-500" : "bg-slate-600 dark:bg-slate-700"
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mt-3">
                  <h3 className="font-semibold text-lg">{getStepTitle()}</h3>
                  <p className="text-slate-300 dark:text-slate-400 text-sm">{getStepDescription()}</p>
                </div>
              </DialogHeader>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Content Type Selection */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(TYPE_META).map(([key, meta], index) => (
                      <motion.div
                        key={`${key}-${index}`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          type="button"
                          variant={type === key ? "default" : "outline"}
                          className={`w-full h-24 p-4 flex flex-col gap-2 transition-all duration-300 ${
                            type === key 
                              ? "bg-slate-800 text-white border-slate-800 shadow-lg dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200" 
                              : "hover:bg-slate-50 hover:border-slate-300 border-slate-200 dark:hover:bg-slate-700 dark:border-slate-600 dark:hover:border-slate-500"
                          }`}
                          onClick={() => setType(key as keyof typeof TYPE_META)}
                        >
                          <div className="text-2xl">{meta.icon}</div>
                          <span className="font-medium">{meta.label}</span>
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 2: Content Details */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Title */}
                  

                  {/* Type-specific fields */}
                  {renderTypeSpecificFields()}
                </motion.div>
              )}

              {/* Step 3: Categorization */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Category Selection */}
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Choose a Category
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {CATEGORIES.filter((c: any) => c._id !== "all").map((cat: any, index: number) => (
                        <motion.div
                          key={`${cat._id}-${index}`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            type="button"
                            variant={category === cat._id ? "default" : "outline"}
                            className={`w-full h-16 p-3 flex flex-col items-center gap-1 transition-all duration-300 ${
                              category === cat._id 
                                ? "bg-slate-800 text-white border-slate-800 shadow-lg dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200" 
                                : "hover:bg-slate-50 border-slate-200 dark:hover:bg-slate-700 dark:border-slate-600"
                            }`}
                            onClick={() => setCategory(cat._id)}
                          >
                            <span className="text-lg">{cat.icon}</span>
                            <span className="text-xs font-medium">{cat.name}</span>
                          </Button>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-2">
                    <Label htmlFor="tags" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Add Tags (optional)
                    </Label>
                    <Input
                      id="tags"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="#productivity, #mindset, #habits (separate with commas)"
                      className="h-12 border rounded-lg transition-colors text-base border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
                      disabled={isLoading}
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Add relevant tags to help others discover your content. The # is optional — with or without it works the same.
                    </p>
                  </div>

                  {/* Preview Card */}
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Preview</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-slate-100 border-slate-300 dark:bg-slate-600 dark:border-slate-500 text-slate-700 dark:text-slate-300">
                          {TYPE_META[type]?.icon}
                          <span className="ml-1">{TYPE_META[type]?.label}</span>
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                          {CATEGORIES.find((c: any) => c._id === category)?.icon} {category}
                        </Badge>
                      </div>
                      <h5 className="font-medium text-slate-900 dark:text-slate-100">
                        {title || "Your title will appear here"}
                      </h5>
                      {tags && (
                        <div className="flex flex-wrap gap-1">
                          {Array.from(
                            new Set(
                              tags
                                .split(",")
                                .map((tag) => tag.trim().toLowerCase().replace(/^#+/, ""))
                                .filter(Boolean)
                            )
                          ).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-6 border-t border-slate-200 dark:border-slate-700 mt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={step === 1 ? handleClose : handleBack}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {step === 1 ? (
                  <>
                    <X className="w-4 h-4" />
                    Cancel
                  </>
                ) : (
                  <>
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </>
                )}
              </Button>

              <div className="flex gap-2">
                {step < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={isLoading}
                    className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 text-white dark:text-slate-900 px-6"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-900 hover:to-black dark:from-slate-200 dark:to-slate-100 dark:hover:from-slate-100 dark:hover:to-white text-white dark:text-slate-900 px-8 shadow-lg"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Create
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Enhanced Auth Modal with Forgot Password
type PendingVerificationState = {
  email: string;
  userId: number;
  username?: string;
  message?: string;
  flow?: "login" | "signup";
};

type AuthView = "login" | "signup" | "forgot" | "verify";

const toAuthView = (mode?: string): AuthView =>
  mode === "signup" || mode === "forgot" ? mode : "login";

function AuthModal({
  open,
  mode = "login",
  onClose,
  onSuccess,
}: {
  open: boolean;
  mode?: "login" | "signup" | "forgot" | string;
  onClose?: () => void;
  onSuccess?: (user: any, method?: "google" | "email") => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [view, setView] = useState<AuthView>(() => toAuthView(mode));
  const [pendingVerification, setPendingVerification] = useState<PendingVerificationState | null>(null);
  // Prefills the forgot-password form, e.g. after "This account uses Google sign-in".
  const [resetEmail, setResetEmail] = useState("");
  const [isGoogleOnlyAccount, setIsGoogleOnlyAccount] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGlobalError(null);
    setIsGoogleOnlyAccount(false);
    setPendingVerification(null);
    setView(toAuthView(mode));
  }, [mode, open]);

  const switchView = (next: AuthView) => {
    setGlobalError(null);
    setIsGoogleOnlyAccount(false);
    setView(next);
  };

  const completeAuth = (data: any, message: string) => {
    if (!data?.token) {
      throw new Error("Invalid response from server");
    }
    onSuccess?.(data, "email");
    showSuccessToast(message);
    setPendingVerification(null);
    onClose?.();
  };

  // Login and signup both answer with requiresVerification when the email still needs an OTP.
  const handleCredentialsResponse = (data: any, flow: "login" | "signup") => {
    if (data?.requiresVerification) {
      setPendingVerification({
        email: data.email,
        userId: data.userId,
        username: data.username,
        message: data.message,
        flow,
      });
      setView("verify");
      showInfoToast(data.message || "We sent a 6-digit code to your email.");
      return;
    }
    completeAuth(data, "Welcome back!");
  };

  const runAuthRequest = async (request: () => Promise<void>) => {
    setGlobalError(null);
    setIsLoading(true);
    try {
      await request();
    } catch (error: any) {
      const message = error?.message || "Something went wrong. Please try again.";
      setGlobalError(message);
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (formData: { email: string; password: string }) =>
    runAuthRequest(async () => {
      setIsGoogleOnlyAccount(false);
      try {
        const data = await ApiService.login({ email: formData.email.trim(), password: formData.password });
        handleCredentialsResponse(data, "login");
      } catch (error: any) {
        // Accounts created with Google have no password; offer to set one by email code.
        if (/uses Google sign-in/i.test(error?.message || "")) {
          setResetEmail(formData.email.trim());
          setIsGoogleOnlyAccount(true);
        }
        throw error;
      }
    });

  const handleSignup = (formData: { username: string; email: string; password: string }) =>
    runAuthRequest(async () => {
      const data = await ApiService.signup({
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
      });
      handleCredentialsResponse(data, "signup");
    });

  // Errors are rethrown so VerificationForm can show them inline next to the code inputs.
  const handleVerifyOtp = async (code: string) => {
    if (!pendingVerification) return;
    setIsLoading(true);
    try {
      const data = await ApiService.verifyOtp({ email: pendingVerification.email, code });
      completeAuth(data, "Email verified! Welcome to The Deepam.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingVerification) return;
    setIsLoading(true);
    try {
      const data: any = await ApiService.resendOtp({ email: pendingVerification.email });
      showInfoToast(data?.message || "A fresh code is on the way.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse: { credential?: string }) => {
    setGlobalError(null);
    setIsLoading(true);

    const token = credentialResponse?.credential;
    if (!token) {
      const message = "Google sign-in failed. Please try again.";
      setGlobalError(message);
      showErrorToast(message);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Google sign-in failed");
      }

      if (data?.token) {
        onSuccess?.(data, "google");
        showSuccessToast("Signed in with Google!");
        onClose?.();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Google login error:", error);
      const message = error?.message || "Google sign-in failed";
      setGlobalError(message);
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogChange = (value: boolean) => {
    if (!value && !isLoading) {
      setGlobalError(null);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v: any) => handleDialogChange(Boolean(v))}>
      <DialogContent className="max-w-4xl sm:max-w-4xl p-0 overflow-hidden border-0 bg-white dark:bg-slate-800 shadow-xl rounded-lg max-h-[95vh] overflow-y-auto">
        <DialogHeader className="sr-only">
          <DialogTitle>Authentication</DialogTitle>
          <DialogDescription>Sign in for The Deepam</DialogDescription>
        </DialogHeader>
        <motion.div 
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.98, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="grid md:grid-cols-2"
        >
          {/* Left panel - Visual */}
          <div className="hidden md:block bg-slate-800 dark:bg-slate-900 text-white p-6">
            <div className="h-full flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="mb-4">
                  <InspireLensLogo size="lg" />
                </div>
                <h2 className="text-2xl font-bold mb-3">Welcome to The Deepam</h2>
                <p className="text-slate-300 dark:text-slate-400 mb-6">
                  Join a community of growth-minded individuals sharing wisdom, inspiration, and insights.
                </p>
                <div className="space-y-3">
                  <motion.div 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-slate-700 dark:bg-slate-600 flex items-center justify-center">
                      <span className="text-sm">✨</span>
                    </div>
                    <span className="text-sm">Discover curated content from experts and thought leaders</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-slate-700 dark:bg-slate-600 flex items-center justify-center">
                      <span className="text-sm">🚀</span>
                    </div>
                    <span className="text-sm">Share your insights and inspire others on their journey</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-slate-700 dark:bg-slate-600 flex items-center justify-center">
                      <span className="text-sm">💡</span>
                    </div>
                    <span className="text-sm">Connect with like-minded individuals and grow together</span>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right panel - Forms */}
          <div className="p-6">
            {(view === "login" || view === "signup") && (
              <>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200">
                    {view === "login" ? "Welcome back" : "Create your account"}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
                    {view === "login"
                      ? "Sign in to continue to The Deepam."
                      : "Join The Deepam. We'll email you a code to verify your address."}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-1 p-1 mb-5 rounded-lg bg-slate-100 dark:bg-slate-700">
                  {(["login", "signup"] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => switchView(tab)}
                      disabled={isLoading}
                      className={`h-9 rounded-md text-sm font-medium transition-colors ${
                        view === tab
                          ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                          : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      {tab === "login" ? "Sign in" : "Sign up"}
                    </button>
                  ))}
                </div>

                {view === "login" ? (
                  <LoginForm
                    onSuccess={handleLogin}
                    isLoading={isLoading}
                    onGoogleLogin={handleGoogleLogin}
                    onForgotPassword={(email) => {
                      setResetEmail(email);
                      switchView("forgot");
                    }}
                  />
                ) : (
                  <SignupForm onSuccess={handleSignup} isLoading={isLoading} onGoogleLogin={handleGoogleLogin} />
                )}
              </>
            )}

            {view === "verify" && pendingVerification && (
              <VerificationForm
                email={pendingVerification.email}
                message={pendingVerification.message}
                onSubmit={handleVerifyOtp}
                onResend={handleResendOtp}
                onBack={() => {
                  const backTo = pendingVerification.flow || "login";
                  setPendingVerification(null);
                  switchView(backTo);
                }}
                isLoading={isLoading}
              />
            )}

            {view === "forgot" && (
              <ForgotPasswordForm
                initialEmail={resetEmail}
                onBack={() => switchView("login")}
                onSuccess={(user) => completeAuth(user, "You're signed in with your new password.")}
              />
            )}

            {globalError && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 text-sm text-red-500 text-center flex items-center justify-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                {globalError}
              </motion.p>
            )}

            {isGoogleOnlyAccount && view === "login" && (
              <Button
                type="button"
                variant="outline"
                onClick={() => switchView("forgot")}
                className="w-full h-10 mt-3"
              >
                Set a password for this account
              </Button>
            )}

            <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 text-center">
              By continuing, you agree to our{" "}
              <Link to="/terms" onClick={() => onClose?.()} className="underline hover:text-slate-700 dark:hover:text-slate-300">
                Terms of Service
              </Link>
              {" "}and{" "}
              <Link to="/privacy" onClick={() => onClose?.()} className="underline hover:text-slate-700 dark:hover:text-slate-300">
                Privacy Policy
              </Link>
            </p>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}

function LoginForm({
    onSuccess,
    isLoading,
    onGoogleLogin,
    onForgotPassword,
  }: {
    onSuccess: (formData: { username: string; email: string; password: string }) => void;
    isLoading?: boolean;
    onGoogleLogin?: (credentialResponse: any) => void;
    onForgotPassword?: (email: string) => void;
  }) {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!password) {
      newErrors.password = "Password is required";
    }
    
    return newErrors;
  };

const handleSubmitLogin = (e: React.FormEvent<HTMLFormElement>) => {
  //console.log("Submitting login form:", { email, password });
  e.preventDefault();

  // Validate form
  const formErrors = validateForm();
  //console.log("Form errors:", formErrors);

  if (Object.keys(formErrors).length > 0) {
    setErrors(formErrors);
    return;
  }

  // Clear previous errors
  setErrors({});

  // Optional: hash password before sending
  // const salt = bcrypt.genSaltSync(10);
  // const hashedPassword = bcrypt.hashSync(password, salt);

  // Call success callback
  onSuccess({
    username: email.split("@")[0],
    email,
    password: password || "" // replace with hashedPassword if hashing
  });
};




const handleGoogleLogin = async (credentialResponse: any) => {
  // console.log("Credential response:", credentialResponse);

  if (!credentialResponse?.credential) {
    console.warn("No credential returned from Google", credentialResponse);
    return;
  }

  if (onGoogleLogin) {
    // console.log("Calling onGoogleLogin callback:", onGoogleLogin);
    try {
      onGoogleLogin(credentialResponse);
    } catch (error) {
      console.error("Error in onGoogleLogin:", error);
    }
  }
};



  return (
  <form onSubmit={handleSubmitLogin} className="space-y-4">
    {/* <GoogleOAuthProvider clientId={CLIENT_ID}> */}
   <div className="w-full">
          <GoogleSignInButton
            onSuccess={handleGoogleLogin}
            onError={() => setErrors({ google: "Google sign-in didn't work. You can use your email and password below." })}
            text="continue_with"
            shape="pill"
            width={400}
            containerProps={{ className: "flex items-center justify-center w-full h-12 rounded-xl border border-gray-300 bg-white shadow-sm cursor-pointer hover:shadow-md hover:bg-gray-50 active:shadow-sm transition-all duration-200 ease-in-out" }}
          />
          {errors?.google && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-600 text-sm mt-1 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.google}
            </motion.p>
          )}
        </div>
    {/* </GoogleOAuthProvider> */}

    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-slate-200 dark:border-slate-600"></div>
      </div>
      <div className="relative flex justify-center text-sm">
        <span className="px-3 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400">
          or continue with email
        </span>
      </div>
    </div>

    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 text-sm">
          Email address
        </Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors(prev => ({ ...prev, email: null }));
          }}
          placeholder="you@example.com"
          className={`h-10 border rounded transition-colors ${
            errors.email
              ? "border-red-300 focus:border-red-500"
              : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
          }`}
          disabled={isLoading}
        />
        {errors.email && (
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-600 text-sm flex items-center gap-1"
          >
            <AlertCircle className="w-3 h-3" />
            {errors.email}
          </motion.p>
        )}
      </div>

      <div className="space-y-1">
        <Label htmlFor="password" className="text-slate-700 dark:text-slate-300 text-sm">
          Password
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors(prev => ({ ...prev, password: null }));
            }}
            placeholder="Enter your password"
            className={`h-10 border rounded pr-10 transition-colors ${
              errors.password
                ? "border-red-300 focus:border-red-500"
                : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
            }`}
            disabled={isLoading}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </Button>
        </div>
        {errors.password && (
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-600 text-sm flex items-center gap-1"
          >
            <AlertCircle className="w-3 h-3" />
            {errors.password}
          </motion.p>
        )}
      </div>
    </div>

    <div className="flex items-center justify-between text-sm">
      <button
        type="button"
        onClick={() => onForgotPassword?.(email.trim())}
        className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
      >
        Forgot password?
      </button>
    </div>

    <Button
      type="submit"
      className="w-full h-10 bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 text-white dark:text-slate-900 rounded disabled:opacity-50 transition-all duration-200"
      disabled={isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Signing in...
        </>
      ) : (
        "Sign in"
      )}
    </Button>
  </form>
);
}

function SignupForm({
  onSuccess,
  isLoading,
  onGoogleLogin,
}: {
  onSuccess: (formData: { username: string; email: string; password: string }) => void;
  isLoading?: boolean;
  onGoogleLogin?: (credentialResponse: any) => void;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!username.trim()) {
      newErrors.username = "Username is required";
    } else if (!validateUsername(username)) {
      newErrors.username = "Username must be 3-20 characters, letters, numbers, and underscores only";
    }
    
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(password)) {
      newErrors.password = "Password must be at least 8 characters with letters and numbers";
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
  //   const salt = bcrypt.genSaltSync(10);
  // const hashedPassword = bcrypt.hashSync(password, salt);
    
    setErrors({});
    onSuccess({ username, email, password: password });
  };

const handleGoogleSignup = async (credentialResponse: any) => {
  //console.log("Credential response:", credentialResponse);

  if (!credentialResponse?.credential) {
    console.warn("No credential returned from Google", credentialResponse);
    return;
  }

  if (onGoogleLogin) {
    //console.log("Calling onGoogleLogin callback:", onGoogleLogin);
    try {
      onGoogleLogin(credentialResponse);
    } catch (error) {
      console.error("Error in onGoogleLogin:", error);
    }
  }
};




  return (


      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Google Signup Button */}
        <div className="w-full">
          <GoogleSignInButton
            onSuccess={handleGoogleSignup}
            onError={() => setErrors({ google: "Google sign-in didn't work. You can use your email and password below." })}
            text="continue_with"
            shape="pill"
            width={400}
            containerProps={{ className: "flex items-center justify-center w-full h-12 rounded-xl border border-gray-300 bg-white shadow-sm cursor-pointer hover:shadow-md hover:bg-gray-50 active:shadow-sm transition-all duration-200 ease-in-out" }}
          />
          {errors?.google && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-600 text-sm mt-1 flex items-center gap-1"
            >
              <AlertCircle className="w-3 h-3" />
              {errors.google}
            </motion.p>
          )}
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200 dark:border-slate-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              or create your account
            </span>
          </div>
        </div>

        {/* Username */}
        <div className="space-y-1">
          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (errors?.username) setErrors((prev) => ({ ...prev, username: null }));
            }}
            placeholder="Enter your username"
            disabled={isLoading}
          />
        </div>

        {/* Email */}
        <div className="space-y-1">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors?.email) setErrors((prev) => ({ ...prev, email: null }));
            }}
            placeholder="you@example.com"
            disabled={isLoading}
          />
        </div>

        {/* Password */}
          <div className="space-y-1">
        <Label htmlFor="password" className="text-slate-700 dark:text-slate-300 text-sm">
          Password
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors(prev => ({ ...prev, password: null }));
            }}
            placeholder="Enter your password"
            className={`h-10 border rounded pr-10 transition-colors ${
              errors.password
                ? "border-red-300 focus:border-red-500"
                : "border-slate-300 focus:border-slate-500 dark:border-slate-600 dark:focus:border-slate-400"
            }`}
            disabled={isLoading}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </Button>
        </div>
      </div>

        {/* Confirm Password */}
        <div className="space-y-1">
          <Label htmlFor="confirmPassword" className="text-slate-700 dark:text-slate-300 text-sm">Confirm Password</Label>
          <div className="relative">
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errors?.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: null }));
            }}
            placeholder="Confirm your password"
            disabled={isLoading}
          />
          </div>
        </div>

        {/* Submit */}
        <Button type="submit" disabled={isLoading} className="w-full h-10">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating account...
            </>
          ) : (
            "Create account"
          )}
        </Button>
      </form>

  );
}

function VerificationForm({
  email,
  message,
  onSubmit,
  onResend,
  onBack,
  isLoading,
}: {
  email: string;
  message?: string;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  isLoading?: boolean;
}) {
  const [codeDigits, setCodeDigits] = useState<string[]>(() => Array(6).fill(""));
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setCodeDigits(Array(6).fill(""));
    setLocalError(null);
    inputRefs.current[0]?.focus();
  }, [email]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setInterval(() => {
      setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendTimer]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    setLocalError(null);
    setCodeDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const paste = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!paste) return;
    const digits = paste.split("");
    setCodeDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < 6; i += 1) {
        next[i] = digits[i] || "";
      }
      return next;
    });
    if (paste.length === 6) {
      void onSubmit(paste).catch((err) => {
        const message = err instanceof Error ? err.message : "Verification failed. Please try again.";
        setLocalError(message);
      });
    }
  };

  const code = codeDigits.join("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (code.length !== 6) {
      setLocalError("Enter the 6-digit code from your email.");
      return;
    }
    try {
      await onSubmit(code);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Verification failed. Please try again.";
      setLocalError(message);
    }
  };

  const handleResend = async () => {
    if (isLoading || resendTimer > 0) return;
    try {
      await onResend();
      setLocalError(null);
      setResendTimer(45);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to resend the code.";
      setLocalError(message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 text-center text-white shadow-2xl border border-slate-800">
        <div className="flex items-center justify-center gap-3 mb-3">
          <InspireLensLogo size="lg" />
          <InspireStackLogo width={64} height={64} />
        </div>
        <h3 className="text-lg font-semibold tracking-tight">Enter your The Deepam code</h3>
        <p className="text-sm text-slate-300 mt-1">We sent a code to keep your account safe.</p>
      </div>

      <div className="rounded-2xl bg-white dark:bg-slate-900 p-4 border border-slate-100 dark:border-slate-800 shadow">
        <p className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-slate-500" />
          {message || "Enter the code we sent to your email."}
        </p>
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1 break-words">{email}</p>
      </div>

      <div className="flex justify-between gap-2">
        {codeDigits.map((digit, index) => (
          <input
            key={`otp-${index}`}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            className="w-12 h-14 rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 text-center text-2xl font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
        ))}
      </div>

      {localError && (
        <motion.p
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-500 flex items-center gap-1"
        >
          <AlertCircle className="w-4 h-4" />
          {localError}
        </motion.p>
      )}

      <Button
        type="submit"
        className="w-full h-11 font-semibold"
        disabled={isLoading || code.length !== 6}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Verifying...
          </>
        ) : (
          "Verify & continue"
        )}
      </Button>

      <div className="text-sm text-center text-slate-600 dark:text-slate-400">
        Didn&apos;t get the code?{" "}
        <button
          type="button"
          className="underline font-medium disabled:opacity-50"
          onClick={handleResend}
          disabled={isLoading || resendTimer > 0}
        >
          {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
        </button>
      </div>

      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={onBack}
        disabled={isLoading}
      >
        Back
      </Button>
    </form>
  );
}

function ForgotPasswordForm({
  initialEmail = "",
  onBack,
  onSuccess,
}: {
  initialEmail?: string;
  onBack: () => void;
  onSuccess?: (user: any) => void;
}) {
  const [step, setStep] = useState<"request" | "reset">("request");
  const [email, setEmail] = useState<string>(initialEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [codeDigits, setCodeDigits] = useState<string[]>(() => Array(6).fill(""));
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [resendTimer, setResendTimer] = useState<number>(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (step === "reset") {
      inputRefs.current[0]?.focus();
    }
  }, [step]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setInterval(() => {
      setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setGlobalError(null);

    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError(null);
    setIsRequesting(true);
    try {
      await ApiService.forgotPassword(email.trim());
      showSuccessToast("We sent you a 6-digit code. Check your inbox.");
      setStep("reset");
      setCodeDigits(Array(6).fill(""));
      setResendTimer(45);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === "string" ? err : "Unable to send reset code right now.";
      setGlobalError(message);
      showErrorToast(message);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || !email) return;
    try {
      await ApiService.forgotPassword(email);
      showInfoToast("A fresh code is on the way.");
      setResendTimer(45);
      setCodeDigits(Array(6).fill(""));
      setGlobalError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === "string" ? err : "Unable to resend the code right now.";
      setGlobalError(message);
      showErrorToast(message);
    }
  };

  const code = codeDigits.join("");

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    setCodeDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const paste = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!paste) return;
    const digits = paste.split("");
    setCodeDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < 6; i += 1) {
        next[i] = digits[i] || "";
      }
      return next;
    });
  };

  const handleResetSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setGlobalError(null);

    if (code.length !== 6) {
      setGlobalError("Enter the 6-digit code we emailed you.");
      return;
    }
    if (!validatePassword(password)) {
      setGlobalError("Password must be at least 8 characters with letters and numbers.");
      return;
    }
    if (password !== confirmPassword) {
      setGlobalError("Passwords do not match.");
      return;
    }

    setIsResetting(true);
    try {
      const response = await ApiService.resetPassword({
        email,
        code,
        password,
      });
      showSuccessToast("Password updated successfully!");
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === "string" ? err : "Unable to reset your password right now.";
      setGlobalError(message);
      showErrorToast(message);
    } finally {
      setIsResetting(false);
    }
  };

  const renderHeaderCard = (title: string, subtitle: string) => (
    <div className="rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 text-center text-white shadow-2xl border border-slate-800">
      <div className="flex items-center justify-center gap-3 mb-3">
        <InspireLensLogo size="lg" />
        <InspireStackLogo width={64} height={64} />
      </div>
      <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
      <p className="text-sm text-slate-300 mt-1">{subtitle}</p>
    </div>
  );

  return (
    <div className="space-y-5">
      {step === "request" ? (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          {renderHeaderCard("Forgot your password?", "Enter your The Deepam email to get a secure 6-digit code.")}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 shadow-lg">
            <Label htmlFor="forgot-email" className="text-sm font-medium text-slate-800 dark:text-slate-200">
              Email address
            </Label>
            <Input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              placeholder="you@example.com"
              className={`mt-2 h-11 rounded-xl ${
                emailError ? "border-red-400 focus-visible:ring-red-400" : "border-slate-200 dark:border-slate-700"
              }`}
            />
            {emailError && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-500 mt-2 flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                {emailError}
              </motion.p>
            )}
            <Button type="submit" className="w-full h-11 mt-4" disabled={isRequesting}>
              {isRequesting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending magic link...
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Send reset code
                </>
              )}
            </Button>
            <Button type="button" variant="ghost" className="w-full mt-2" onClick={onBack}>
              Back to login
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleResetSubmit} className="space-y-5">
          {renderHeaderCard("Check your inbox", `Enter the code sent to ${email}`)}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 shadow-lg space-y-5">
            <div className="flex justify-between gap-2">
              {codeDigits.map((digit, index) => (
                <input
                  key={`reset-otp-${index}`}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-14 rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 text-center text-2xl font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              ))}
            </div>
            <div className="text-center text-sm text-slate-600 dark:text-slate-400">
              Didn't receive it?{" "}
              <button
                type="button"
                onClick={handleResend}
                className="font-semibold underline disabled:opacity-50"
                disabled={resendTimer > 0}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="new-password" className="text-sm text-slate-600 dark:text-slate-300">
                  New password
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  className="h-11 rounded-xl border-slate-200 dark:border-slate-700"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm-password" className="text-sm text-slate-600 dark:text-slate-300">
                  Confirm password
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  className="h-11 rounded-xl border-slate-200 dark:border-slate-700"
                />
              </div>
            </div>
            {globalError && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-500 flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                {globalError}
              </motion.p>
            )}
            <Button type="submit" className="w-full h-11" disabled={isResetting || code.length !== 6}>
              {isResetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating password...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Verify & update password
                </>
              )}
            </Button>
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline"
              onClick={() => {
                setStep("request");
                setCodeDigits(Array(6).fill(""));
                setPassword("");
                setConfirmPassword("");
                setGlobalError(null);
              }}
            >
              Use a different email
            </button>
          </div>
          <Button onClick={onBack} type="button" variant="ghost" className="w-full">
            Back to login
          </Button>
        </form>
      )}
      {globalError && step === "request" && (
        <motion.p
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-500 flex items-center gap-2"
        >
          <AlertCircle className="w-4 h-4" />
          {globalError}
        </motion.p>
      )}
    </div>
  );
}

// Floating Action Button
function FloatingActionButton({
  onClick,
  currentUser,
}: {
  onClick: () => void;
  currentUser?: { id?: number; username?: string; name?: string } | null;
}): React.ReactElement | null {
  if (!currentUser) return null;

  return (
    <motion.div
      className="fixed bottom-6 right-6 z-50"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1, type: "spring", stiffness: 200, damping: 15 }}
    >
      <Button
        onClick={onClick}
        className="w-12 h-12 bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 text-white dark:text-slate-900 rounded-full shadow-lg flex items-center justify-center transition-all duration-200"
        size="icon"
        aria-label="Create new content"
      >
        <Plus className="w-5 h-5" />
      </Button>
    </motion.div>
  );
}



function PostDetail(){
  return (
    <div className="p-6 max-w-3xl relative mx-auto space-y-4 bg-white dark:bg-slate-800 rounded-lg shadow">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Post Detail Page</h1>
      <p className="text-gray-600 dark:text-gray-400">This is where the post details will be displayed.</p>
      <span className="absolute top-4 right-4 cursor-pointer text-gray-600 dark:text-gray-400 p-2 rounded-xl shadow"> x </span>
      <div className="space-y-4">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Post Title</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
          <div className="mt-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">Posted on June 1, 2024 by Author Name</span>
          </div>
        </div>
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Another Post Title</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
          </p>
          <div className="mt-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">Posted on May 28, 2024 by Another Author</span>
          </div>
        </div>
      </div>
    </div>

  );
}




// Main App
function AppContent() {
  const [items, setItems] = useState<FeedItem[]>(initialItems || []);

  // The feed's category/type/sort/page are derived from the URL rather than
  // held in state, so every view has its own shareable link:
  //   /category/mindset?type=quote&page=2   /?sort=recent   /?type=prompt
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryMatch = matchPath("/category/:categorySlug", location.pathname);
  const isFeedRoute = location.pathname === "/" || !!categoryMatch;
  const activeCategory = categoryIdFromParam(categoryMatch?.params.categorySlug);
  const activeType = feedTypeFromParam(searchParams.get("type"));
  const requestedSort = feedSortFromParam(searchParams.get("sort"));
  // Sort applies within whichever category/type filter is active (e.g.
  // "Articles" + "Liked" shows liked articles) instead of being reset by it.
  const feedSort: FeedSortState = requestedSort;
  const currentPage = parsePageParam(searchParams.get("page"));
  // Set by the "Translate Quotes" button - every quote card on the page
  // switches to this language (see FeedList's globalQuoteLanguage prop).
  // Comes from ?lang= so a shared link opens in the same language.
  const [quoteLanguage, setQuoteLanguage, hasLangParam] = useUrlLanguage();
  // Carried through feed navigation once chosen, so switching category/type/
  // page doesn't silently drop back to English.
  const langParam = hasLangParam ? quoteLanguage : undefined;
  const [isLoading, setIsLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() => {
    try {
      return JSON.parse(localStorage.getItem("inspirelens_user") || "null");
    } catch {
      return null;
    }
  });
  const [digestSubscribed, setDigestSubscribed] = useState<boolean | null>(null);
  const [isUpdatingDigestPreference, setIsUpdatingDigestPreference] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const welcomeBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoginPromptVisible, setIsLoginPromptVisible] = useState(false);
  const [showLoginPromptBadge, setShowLoginPromptBadge] = useState(false);
  const [autoCloseSecondsLeft, setAutoCloseSecondsLeft] = useState(AUTO_CLOSE_SECONDS);
  const [, setAutoReopenQuota] = useState(1);
  const countdownIntervalRef = useRef<number | null>(null);
  const loginPromptTimerRef = useRef<number | null>(null);
  const reopenBadgeTimerRef = useRef<number | null>(null);

  // Slide the welcome banner in on login, then auto-dismiss it after 10s.
  useEffect(() => {
    if (welcomeBannerTimerRef.current) {
      clearTimeout(welcomeBannerTimerRef.current);
      welcomeBannerTimerRef.current = null;
    }

    if (!currentUser?.id) {
      setShowWelcomeBanner(false);
      return;
    }

    setShowWelcomeBanner(true);
    welcomeBannerTimerRef.current = setTimeout(() => {
      setShowWelcomeBanner(false);
      welcomeBannerTimerRef.current = null;
    }, 10000);

    return () => {
      if (welcomeBannerTimerRef.current) {
        clearTimeout(welcomeBannerTimerRef.current);
        welcomeBannerTimerRef.current = null;
      }
    };
  }, [currentUser?.id]);

  const emitDigestPreference = useCallback((value: boolean | null) => {
    if (typeof window === "undefined") return;
    if (value === null) {
      localStorage.removeItem("inspirelens_digest_subscribed");
    } else {
      localStorage.setItem("inspirelens_digest_subscribed", value ? "true" : "false");
    }
    window.dispatchEvent(new CustomEvent("inspirelens:digestPreferenceChanged", { detail: value }));
  }, []);

  const clearLoginPromptTimer = useCallback(() => {
    if (loginPromptTimerRef.current) {
      clearTimeout(loginPromptTimerRef.current);
      loginPromptTimerRef.current = null;
    }
  }, []);

  const resetAutoCloseCountdown = useCallback(() => {
    setAutoCloseSecondsLeft(AUTO_CLOSE_SECONDS);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const triggerLoginPrompt = useCallback(() => {
    if (currentUser?.id) return;
    clearLoginPromptTimer();
    resetAutoCloseCountdown();
    setIsLoginPromptVisible(true);
    setShowLoginPromptBadge(false);
  }, [currentUser?.id, clearLoginPromptTimer, resetAutoCloseCountdown]);

  const scheduleLoginPrompt = useCallback(
    (delay = 5000) => {
      if (currentUser?.id) return;
      clearLoginPromptTimer();

      loginPromptTimerRef.current = window.setTimeout(() => {
        triggerLoginPrompt();
      }, delay);
    },
    [clearLoginPromptTimer, currentUser?.id, triggerLoginPrompt]
  );

  const resetAutoReopen = useCallback(() => {
    setAutoReopenQuota(1);
  }, []);

  const attemptAutoReopen = useCallback(() => {
    setAutoReopenQuota((prev) => {
      if (prev <= 0) return prev;
      scheduleLoginPrompt(5000);
      return prev - 1;
    });
  }, [scheduleLoginPrompt]);

  const dispatchUserChange = useCallback((user: CurrentUser | null) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("inspirelens:userchange", { detail: user }));
  }, []);

  const handleLogout = useCallback(
    ({ message, showSuccess = true }: { message?: string; showSuccess?: boolean } = {}) => {
      setCurrentUser(null);
      localStorage.removeItem("inspirelens_user");
      setDigestSubscribed(null);
      emitDigestPreference(null);
      dispatchUserChange(null);
      if (showSuccess) {
        showSuccessToast("You have been logged out successfully");
      } else if (message) {
        showErrorToast(message);
      }
      resetAutoReopen();
    },
    [resetAutoReopen, dispatchUserChange, emitDigestPreference]
  );

  useEffect(() => {
    const handleTokenExpired = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      handleLogout({
        showSuccess: false,
        message: detail?.message || "Session expired. Please log in again.",
      });
    };

    if (typeof window === "undefined") return;
    window.addEventListener("auth:tokenexpired", handleTokenExpired);
    return () => {
      window.removeEventListener("auth:tokenexpired", handleTokenExpired);
    };
  }, [handleLogout]);

  const openAuth = useCallback(
    (mode = "login") => {
      if (mode === "logout") {
        handleLogout();
        return;
      }
      if (mode === "delete-account") {
        setDeleteAccountOpen(true);
        return;
      }
      isAuthHandledRef.current = false;
      setAuthMode(mode);
      setAuthOpen(true);
    },
    [handleLogout]
  );

  const handleCloseLoginPrompt = useCallback(
    ({ allowReopen = false } = {}) => {
      resetAutoCloseCountdown();
      setIsLoginPromptVisible(false);

      if (!allowReopen) {
        setShowLoginPromptBadge(false);
        if (reopenBadgeTimerRef.current) {
          clearTimeout(reopenBadgeTimerRef.current);
          reopenBadgeTimerRef.current = null;
        }
        return;
      }

      setShowLoginPromptBadge(true);
      if (reopenBadgeTimerRef.current) {
        clearTimeout(reopenBadgeTimerRef.current);
      }
      reopenBadgeTimerRef.current = window.setTimeout(() => {
        setShowLoginPromptBadge(false);
        reopenBadgeTimerRef.current = null;
      }, 20000);

      attemptAutoReopen();
    },
    [attemptAutoReopen, resetAutoCloseCountdown]
  );

  // Android Back closes the login prompt instead of leaving the screen.
  useEffect(() => {
    if (!isLoginPromptVisible) return;
    const handleNativeBack = (event: Event) => {
      event.preventDefault();
      handleCloseLoginPrompt();
    };
    window.addEventListener(NATIVE_BACK_EVENT, handleNativeBack);
    return () => window.removeEventListener(NATIVE_BACK_EVENT, handleNativeBack);
  }, [isLoginPromptVisible, handleCloseLoginPrompt]);

  useEffect(() => {
    const goHome = () => navigate("/", { replace: true });
    window.addEventListener(NATIVE_GO_HOME_EVENT, goHome);
    return () => window.removeEventListener(NATIVE_GO_HOME_EVENT, goHome);
  }, [navigate]);

  // Tapping a push notification opens its page (a post, comment or profile).
  // A notification for an account waits for sign-in when nobody is signed in.
  const pendingPushPathRef = useRef<string | null>(null);
  useEffect(() => {
    const openPath = (event: Event) => {
      const { path, personal, notificationId } = (event as CustomEvent<PushOpenPathDetail>).detail;
      if (personal && !currentUser) {
        pendingPushPathRef.current = path;
        openAuth("login");
        return;
      }
      if (notificationId) {
        ApiService.markNotificationRead(notificationId)
          .then(() => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT)))
          .catch(() => {});
      }
      navigate(path);
    };
    window.addEventListener(PUSH_OPEN_PATH_EVENT, openPath);
    return () => window.removeEventListener(PUSH_OPEN_PATH_EVENT, openPath);
  }, [navigate, currentUser, openAuth]);
  useEffect(() => {
    if (!currentUser || !pendingPushPathRef.current) return;
    const path = pendingPushPathRef.current;
    pendingPushPathRef.current = null;
    navigate(path);
  }, [currentUser, navigate]);

  const handleLoginPromptContinue = useCallback(() => {
    resetAutoReopen();
    openAuth("login");
    handleCloseLoginPrompt({ allowReopen: true });
  }, [resetAutoReopen, openAuth, handleCloseLoginPrompt]);

  const [showWelcome, setShowWelcome] = useState(true);
  function handleCloseWelcome() {
    setShowWelcome(false);
  }

  const handleReopenLoginPrompt = useCallback(() => {
    if (reopenBadgeTimerRef.current) {
      clearTimeout(reopenBadgeTimerRef.current);
      reopenBadgeTimerRef.current = null;
    }

    if (currentUser?.id) {
      return;
    }

    triggerLoginPrompt();
  }, [triggerLoginPrompt, currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id) {
      setDigestSubscribed(null);
      emitDigestPreference(null);
      return;
    }

    const token = currentUser?.token;
    if (!token) {
      setDigestSubscribed(false);
      emitDigestPreference(false);
      return;
    }

    let isMounted = true;

    const fetchDigestPreference = async () => {
      try {
        const response = await ApiService.getDigestPreference(token);
        if (isMounted) {
          const subscribed = Boolean(response?.subscribed);
          setDigestSubscribed(subscribed);
          emitDigestPreference(subscribed);
        }
      } catch (error) {
        console.error("Failed to fetch digest preference:", error);
        if (isMounted) {
          setDigestSubscribed(false);
          emitDigestPreference(false);
        }
      }
    };

    fetchDigestPreference();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.id, emitDigestPreference]);

  useEffect(() => {
    if (currentUser?.id) {
      setIsLoginPromptVisible(false);
      setShowLoginPromptBadge(false);
      clearLoginPromptTimer();
      resetAutoCloseCountdown();
      return;
    }

    // The app has a Log in tab, so it doesn't interrupt browsing with the prompt.
    if (!isNativeApp) scheduleLoginPrompt(5000);

    return () => {
      clearLoginPromptTimer();
    };
  }, [currentUser?.id, scheduleLoginPrompt, clearLoginPromptTimer, resetAutoCloseCountdown]);

  useEffect(() => {
    if (!isLoginPromptVisible) {
      resetAutoCloseCountdown();
      return;
    }

    resetAutoCloseCountdown();
    countdownIntervalRef.current = window.setInterval(() => {
      setAutoCloseSecondsLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isLoginPromptVisible, resetAutoCloseCountdown]);

  useEffect(() => {
    if (autoCloseSecondsLeft === 0 && isLoginPromptVisible) {
      handleCloseLoginPrompt({ allowReopen: false });
    }
  }, [autoCloseSecondsLeft, isLoginPromptVisible, handleCloseLoginPrompt]);

  useEffect(() => {
    return () => {
      if (loginPromptTimerRef.current) {
        clearTimeout(loginPromptTimerRef.current);
      }
      if (reopenBadgeTimerRef.current) {
        clearTimeout(reopenBadgeTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  // Loads the items for whatever view the URL describes. `initialItems` is the
  // default trending feed fetched at startup, so "/" doesn't refetch on load;
  // returning to an already-loaded view (e.g. Back from a post) doesn't either.
  const feedKey = `${activeCategory}|${activeType}|${feedSort}${feedSort === "liked" ? `|${currentUser?.id ?? ""}` : ""}`;
  const loadedFeedKeyRef = useRef<string | null>(initialItems.length ? "all|all|trending" : null);
  const feedRequestIdRef = useRef(0);
  // Set when the last load failed, so FeedList shows a retry panel instead of
  // "No content found". Bumping feedRetryCount re-runs the load below.
  const [feedLoadError, setFeedLoadError] = useState(false);
  const [feedOffline, setFeedOffline] = useState(false);
  const [feedRetryCount, setFeedRetryCount] = useState(0);
  const retryFeed = useCallback(() => setFeedRetryCount((count) => count + 1), []);

  // After a failed load, try again as soon as the connection or the app comes back.
  useEffect(() => {
    if (!feedLoadError) return;
    let cancelled = false;
    isOnline().then((online) => !cancelled && setFeedOffline(!online));
    const removeReconnectListener = onReconnect(retryFeed);
    const removeResumeListener = onAppResume(retryFeed);
    return () => {
      cancelled = true;
      removeReconnectListener();
      removeResumeListener();
    };
  }, [feedLoadError, retryFeed]);

  useEffect(() => {
    if (!isFeedRoute || loadedFeedKeyRef.current === feedKey) return;

    if (feedSort === "liked" && !currentUser?.id) {
      openAuth("login");
      return;
    }

    const requestId = ++feedRequestIdRef.current;
    const isInitialLoad = loadedFeedKeyRef.current === null;
    setIsLoading(true);
    setFeedLoadError(false);

    (async () => {
      try {
        const hasFilter = activeCategory !== "all" || activeType !== "all";
        const response =
          feedSort === "liked"
            ? await ApiService.getLikedPosts({ type: activeType, category: activeCategory })
            : hasFilter
            ? await ApiService.getContentByCategory(activeCategory, 1, activeType, feedSort === "recent" ? "recent" : "trending")
            : await ApiService.getAllPosts({ sort: feedSort === "recent" ? "recent" : "trending" });
        if (requestId !== feedRequestIdRef.current) return;

        setItems(response?.items || response?.posts || []);
        loadedFeedKeyRef.current = feedKey;
        if (feedRetryCount > 0 && isInitialLoad) showInfoToast("Feed loaded");

        if (!isInitialLoad) {
          const categoryName = activeCategory !== "all" ? categoryNameFromId(activeCategory) : null;
          const typeName = activeType !== "all" ? feedTypeLabel(activeType) : null;
          const sortName = feedSort === "liked" ? "Liked" : feedSort === "recent" ? "Recent" : null;
          const message =
            categoryName || typeName || sortName
              ? `Loaded ${[categoryName, typeName, sortName].filter(Boolean).join(" · ")}`
              : "Showing trending posts";
          showInfoToast(message);
        }
      } catch (error) {
        if (requestId !== feedRequestIdRef.current) return;
        console.error("Failed to load feed:", error);
        // Keep the items of the previous view out of a view that didn't load.
        setItems([]);
        setFeedLoadError(true);
        showErrorToast(
          feedSort === "liked"
            ? "Failed to load your liked content. Please try again."
            : "Failed to load content. Please try again."
        );
      } finally {
        if (requestId === feedRequestIdRef.current) setIsLoading(false);
      }
    })();
  }, [isFeedRoute, feedKey, feedRetryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  // A shared ?page=9 link for a view that now has fewer pages shows the last page.
  const visiblePage = Math.min(currentPage, Math.max(totalPages, 1));

  const paginatedItems = useMemo(() => {
    const startIndex = (visiblePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return items.slice(startIndex, endIndex);
  }, [items, visiblePage]);

  const viewTitle = useMemo(() => {
    const parts = [
      activeCategory !== "all" ? categoryNameFromId(activeCategory) : null,
      activeType !== "all" ? feedTypeLabel(activeType) : null,
    ].filter(Boolean);
    const sortLabel = feedSort === "recent" ? "Recent" : feedSort === "liked" ? "Liked" : null;
    if (parts.length) return sortLabel ? `${parts.join(" · ")} · ${sortLabel}` : parts.join(" · ");
    return sortLabel ?? "Trending";
  }, [activeCategory, activeType, feedSort]);

  const feedTitleRef = useRef(DEFAULT_PAGE_TITLE);
  useEffect(() => {
    if (isFeedRoute) {
      feedTitleRef.current = viewTitle !== "Trending" ? `${viewTitle} · The Deepam` : DEFAULT_PAGE_TITLE;
      document.title = feedTitleRef.current;
    } else if (document.title === feedTitleRef.current) {
      // Pages that set their own title (Tag Cloud, Privacy...) already ran their
      // effect; only reset a feed title the new page left behind.
      document.title = DEFAULT_PAGE_TITLE;
    }
  }, [isFeedRoute, viewTitle]);

  // Filters are links: each change pushes a new URL (so Back/Forward and
  // refresh work) and the effect above loads the matching items.
  const getCategoryHref = useCallback(
    (categoryId: number | string) => buildFeedUrl({ category: categoryId, type: activeType, sort: feedSort, lang: langParam }),
    [activeType, feedSort, langParam]
  );

  const handleCategoryChange = (categoryId: number | string) => {
    navigate(getCategoryHref(categoryId));
  };

  const handleTypeChange = (typeId: string) => {
    navigate(buildFeedUrl({ category: activeCategory, type: feedTypeFromParam(typeId), sort: feedSort, lang: langParam }));
  };

  const handleFeedSortChange = (nextSort: FeedSortOption) => {
    if (nextSort === "liked" && !currentUser) {
      openAuth("login");
      return;
    }
    navigate(buildFeedUrl({ category: activeCategory, type: activeType, sort: nextSort, lang: langParam }));
  };

  const handlePageChange = (page: number) => {
    if (page === visiblePage) return;
    navigate(buildFeedUrl({ category: activeCategory, type: activeType, sort: feedSort, page, lang: langParam }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDigestPreferenceUpdate = useCallback(
    async (subscribe: boolean) => {
      if (!currentUser?.id) {
        openAuth("login");
        return;
      }

      setIsUpdatingDigestPreference(true);
      try {
        const response = await ApiService.updateDigestPreference(subscribe);
        const subscribed = Boolean(response?.subscribed);
        setDigestSubscribed(subscribed);
        emitDigestPreference(subscribed);
        showSuccessToast(
          subscribe
            ? "You're subscribed to the daily digest."
            : "You've unsubscribed from the daily digest."
        );
      } catch (error) {
        console.error("Failed to update digest preference:", error);
        showErrorToast("Unable to update email preference. Please try again.");
      } finally {
        setIsUpdatingDigestPreference(false);
      }
    },
    [currentUser?.id, openAuth, emitDigestPreference]
  );

  const handleSubscribe = useCallback(() => handleDigestPreferenceUpdate(true), [handleDigestPreferenceUpdate]);
  const handleUnsubscribe = useCallback(
    () => handleDigestPreferenceUpdate(false),
    [handleDigestPreferenceUpdate]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleOpenAuth = (event: Event) => {
      const mode = (event as CustomEvent<string | undefined>).detail;
      openAuth(mode);
    };
    const handleOpenCreate = () => setCreateOpen(true);
    const handleSubscribeEvent = () => handleSubscribe();
    const handleUnsubscribeEvent = () => handleUnsubscribe();

    window.addEventListener("inspirelens:openAuth", handleOpenAuth);
    window.addEventListener("inspirelens:openCreateModal", handleOpenCreate);
    window.addEventListener("inspirelens:subscribe", handleSubscribeEvent);
    window.addEventListener("inspirelens:unsubscribe", handleUnsubscribeEvent);

    return () => {
      window.removeEventListener("inspirelens:openAuth", handleOpenAuth);
      window.removeEventListener("inspirelens:openCreateModal", handleOpenCreate);
      window.removeEventListener("inspirelens:subscribe", handleSubscribeEvent);
      window.removeEventListener("inspirelens:unsubscribe", handleUnsubscribeEvent);
    };
  }, [openAuth, handleSubscribe, handleUnsubscribe]);

  const isAuthHandledRef = useRef(false);

  function handleAuthSuccess(user: any, method: "google" | "email" = "google") {
    //console.log("User:",user);
    
    if (isAuthHandledRef.current) return;
    isAuthHandledRef.current = true;

    setCurrentUser(user);
    if (user.token) {
      localStorage.setItem("inspirelens_user", JSON.stringify(user));
    }
    dispatchUserChange(user);
    setAuthOpen(false);

    identifyAnalyticsUser(user);
    const engagementStatus = resolveUserEngagementStatus(user?.id);
    const authEventName = authMode === "signup" ? "sign_up" : "login";
    trackAnalyticsEvent(authEventName, {
      method,
      user_id: user?.id,
      status: engagementStatus,
    });
    trackAnalyticsEvent(
      engagementStatus === "new" ? "unique_user" : "repeat_user",
      {
        user_id: user?.id,
        username: user?.username,
      }
    );
  }

  function handleCreateContent(newItem: FeedItem) {
    const newCategory = newItem.category_id ? String(newItem.category_id) : "all";
    const newType = feedTypeFromParam(String(newItem.type || ""));
    // If the feed is filtered to a different category/type than the new post,
    // switch to the new post's view. Navigating refetches that view, so its
    // cards show the new category's content instead of the previous
    // category's (Bugs/bugs.md #1: only the filter changed, not the items).
    const targetCategory = activeCategory !== "all" && activeCategory !== newCategory ? newCategory : activeCategory;
    const targetType = activeType !== "all" && activeType !== newType ? "all" : activeType;

    if (!isFeedRoute || (targetCategory === activeCategory && targetType === activeType)) {
      setItems((prev) => [newItem, ...prev]);
      if (isFeedRoute && currentPage !== 1) {
        navigate(buildFeedUrl({ category: activeCategory, type: activeType, sort: feedSort, lang: langParam }));
      }
    } else {
      navigate(buildFeedUrl({ category: targetCategory, type: targetType, lang: langParam }));
    }

    // Emit analytics event for new community contributions
    trackAnalyticsEvent("content_created", {
      content_type: newItem.type || "unknown",
      category_id: newItem.category_id || newItem.category || "uncategorized",
      user_id: currentUser?.id || "anonymous",
    });
  }

  // ✅ OPTIMIZED handleVote - only updates the specific voted item
  const handleVote = useCallback((id: number, delta: number, updatedVotes?: Vote[], postType?: string, absolutePoints?: number, updatedTimestamp?: string | number) => {
    setItems((prev: FeedItem[]) => {
      return prev.map((item) => {
        // Skip items that don't match; only enforce type check if provided
        if (item.id !== id || (postType && item.type !== postType)) {
          return item;
        }

        // Update only the matching item
        const oldPoints = normalizePointsCount(item.points_count);
        const nextPoints = typeof absolutePoints === "number" && Number.isFinite(absolutePoints)
          ? Number(absolutePoints)
          : oldPoints + delta;
        const updatedVotesArray = updatedVotes || item.points;

        // Return updated item with immutable update
        const updatedItem: FeedItem = {
          ...item,
          points_count: nextPoints,
          points: updatedVotesArray,
        };

        if (updatedTimestamp) {
          updatedItem.updatedAt = updatedTimestamp;
          updatedItem.updated_at = updatedTimestamp;
        }

        return updatedItem;
      });
    });
  }, []);

  // ✅ OPTIMIZED handleAddComment - only updates the specific commented item
const handleAddComment = useCallback(
  async (
    itemId: number,
    postType: string,
    comment: { text: any; userId: any; user_id?: any; user: string; createdBy: string }
  ) => {
    // console.log("📝 Adding comment:", {
    //   itemId,
    //   postType,
    //   comment,
    // });

    const optimisticComment = {
      ...comment,
      id: Date.now(),
    };

    // console.log("🚀 Optimistic comment created:", optimisticComment);

    // Add comment to UI immediately (optimistic update)
    setItems((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== itemId) return item;

        const newComment = {
          id: optimisticComment.id,
          text: optimisticComment.text,
          user: optimisticComment.user,
          user_id: optimisticComment.user_id ?? optimisticComment.userId,
          created_at: Date.now(),
          createdBy: optimisticComment.createdBy,
        } as Comment;

        // console.log("🧩 Adding optimistic comment to item:", {
        //   itemId: item.id,
        //   newComment,
        // });

        return {
          ...item,
          comments: [...(item.comments ?? []), newComment],
        };
      });

      // console.log("💾 Updated items after optimistic add:", updated);
      return updated;
    });

    try {
      // console.log("📡 Sending comment to server...");
      const response = await ApiService.addComment(
        itemId,
        postType,
        comment.text,
        comment.userId
      );

      // console.log("✅ Server response received:", response);

      // Replace optimistic comment with real one from server
      setItems((prev) => {
        const updated = prev.map((item) => {
          if (item.id !== itemId) return item;

          const updatedComments = Array.isArray(item.comments)
            ? item.comments.map((c) =>
                c.id === optimisticComment.id ? response.comment : c
              )
            : [];

          // console.log("🔄 Replacing optimistic comment:", {
          //   itemId: item.id,
          //   optimisticId: optimisticComment.id,
          //   newComment: response.comment,
          // });

          return { ...item, comments: updatedComments };
        });

        // console.log("💾 Final items after server confirmation:", updated);
        return updated;
      });
    } catch (error) {
      console.error("❌ Failed to add comment:", error);

      // Revert optimistic update on error
      setItems((prev) => {
        const updated = prev.map((item) => {
          if (item.id !== itemId) return item;

          const revertedComments = Array.isArray(item.comments)
            ? item.comments.filter((c) => c.id !== optimisticComment.id)
            : [];

          console.warn("↩️ Reverting optimistic comment:", {
            itemId: item.id,
            optimisticId: optimisticComment.id,
          });

          return { ...item, comments: revertedComments };
        });

        // console.log("💾 Items after revert:", updated);
        return updated;
      });

      showErrorToast("Failed to add comment. Please try again.");
    }
  },
  []
);


  // ✅ OPTIMIZED handleDeleteComment - only updates the specific item
  const handleDeleteComment = useCallback((itemId: number, commentId: number) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) {
          return item;
        }
        
        return {
          ...item,
          comments: Array.isArray(item.comments) 
            ? item.comments.filter(c => c.id !== commentId) 
            : [],
        };
      })
    );
  }, []);
  // OPTIMIZED handleEditPost - only updates the specific edited item
  function handleEditPost(post: FeedItem | number, updatedData: any) {
    console.log("handleEditPost called with post:", post);
    console.log("handleEditPost called with updatedData:", updatedData);
    const postId = typeof post === "number" ? post : post?.id;
    //console.log("handleEditPost called with postId:", postId);
    //console.log("handleEditPost called with updatedData:", updatedData);
    
    if (!postId) {
      console.warn("handleEditPost called without a valid post id");
      return;
    }

    if (!updatedData) {
      setItems((prev) => prev.filter((it) => it.id !== postId));
      showSuccessToast("Post deleted successfully!");
      return;
    }

    setItems((prev) =>
      prev.map((it) => (it.id === postId ? { ...it, ...updatedData } : it))
    );
    showSuccessToast("Post updated successfully!");
  }
  // ✅ OPTIMIZED handleDeletePost - only updates the specific deleted item
  async function handleDeletePost(postId: number, postType: string) {
    try {
      const response = await ApiService.deleteContent(postId, postType);
      if (response?.error) {
        throw new Error(response.error);
      }

      setItems((prev) => prev.filter((it) => it.id !== postId));
      showSuccessToast("Post deleted successfully!");
    } catch (error) {
      console.error("Failed to delete post:", error);
      showErrorToast("Failed to delete post. Please try again.");
      throw error;
    }
  }

  const FeedLayout = () => (
    <div className={isNativeApp ? "mx-auto px-4 pt-3 pb-6" : "max-w-[85rem] mx-auto px-3 py-6"}>
      <Header
        currentUser={currentUser}
        onOpenAuth={openAuth}
        onOpenCreateModal={() => setCreateOpen(true)}
        isSubscribed={digestSubscribed}
        onSubscribe={handleSubscribe}
        onUnsubscribe={handleUnsubscribe}
        isSubscriptionUpdating={isUpdatingDigestPreference}
      />

      <div className={isNativeApp ? "mt-1" : "mt-6"}>
        <WelcomeHero
          currentUser={currentUser}
          postCount={items.length}
          categoryCount={CATEGORIES.length}
          contentTypeCount={Object.keys(TYPE_META).length}
          visible={showWelcomeBanner}
        />
      </div>

      <div className={isNativeApp ? "mt-3 flex flex-col gap-4" : "mt-6 flex flex-col gap-6 md:flex-row"}>
        {/* Fixed-width sidebar (rather than a proportional grid column) so the
            toolbar next to it keeps as much room as possible to lay its
            pills out on a single line. It only widens out to its full size
            once the page is genuinely wide enough to afford it (~1366px+,
            where the wider max-w-[85rem] container actually has the extra
            room) - below that it stays narrow so the toolbar still fits on
            one line instead of wrapping. */}
        <aside className="hidden md:block md:w-44 min-[1366px]:w-60 md:shrink-0">
          <div className="sticky top-6">
            <Sidebar
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              getCategoryHref={getCategoryHref}
              isLoading={isLoading}
            />
          </div>
        </aside>

        <main className="min-w-0 md:flex-1">
          <div className="space-y-3 sm:space-y-4">
            {isNativeApp ? (
              // App toolbar: one swipeable row of type chips, then a single row
              // of view controls (category, sort, translate, share), then a
              // quiet count - instead of the website's wrapping pill rows.
              <div className="space-y-3">
                <ContentTypeFilter activeType={activeType} onTypeChange={handleTypeChange} />
                <div className="flex items-center gap-2">
                  <MobileCategorySidebar
                    activeCategory={activeCategory}
                    onCategoryChange={handleCategoryChange}
                    getCategoryHref={getCategoryHref}
                    isLoading={isLoading}
                    label={activeCategory === "all" ? "All categories" : categoryNameFromId(activeCategory) ?? "Categories"}
                    triggerClassName="h-10 min-w-0 max-w-[34%] px-3.5"
                  />
                  <FeedSortToggle value={feedSort} onChange={handleFeedSortChange} disabled={isLoading} />
                  <div className="ml-auto flex items-center gap-1.5">
                    <TranslateQuotesButton value={quoteLanguage} onChange={setQuoteLanguage} compact />
                    <ShareLinkButton title={viewTitle} compact />
                  </div>
                </div>
                {!feedLoadError && (
                  <p className="px-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {items.length} {items.length === 1 ? "item" : "items"} · Page {visiblePage} of {totalPages || 1}
                  </p>
                )}
              </div>
            ) : (
              <>
              <div className="flex flex-row flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3">
                  <p className="text-slate-600 dark:text-slate-400 text-sm whitespace-nowrap">
                    {items.length} {items.length === 1 ? "item" : "items"}
                  </p>
                  <MobileCategorySidebar
                    activeCategory={activeCategory}
                    onCategoryChange={handleCategoryChange}
                    getCategoryHref={getCategoryHref}
                    isLoading={isLoading}
                  />
                </div>
  
                <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  Page {visiblePage} of {totalPages || 1}
                </span>
              </div>
  
  
              {/* xl:flex-wrap is a safety net: the two groups are sized to
                  their own content (not clamped via flex-grow) so if they
                  ever don't both fit on one line, the right-hand group wraps
                  to its own line below instead of silently overlapping the
                  type pills and hiding them. */}
              <div className="flex flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-center xl:justify-between xl:gap-x-2">
                <ContentTypeFilter
                  activeType={activeType}
                  onTypeChange={handleTypeChange}
                />
  
                <div className="flex items-center gap-1.5 shrink-0">
                  <TranslateQuotesButton value={quoteLanguage} onChange={setQuoteLanguage} />
                  <div className="hidden sm:block h-5 w-px bg-slate-300 dark:bg-slate-700" aria-hidden="true" />
                  <FeedSortToggle
                    value={feedSort}
                    onChange={handleFeedSortChange}
                    disabled={isLoading}
                  />
                  <ShareLinkButton title={viewTitle} />
                </div>
              </div>
              </>
            )}

            <Suspense
              fallback={
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="w-full h-48 rounded-2xl" />
                  ))}
                </div>
              }
            >
              <FeedList
                items={paginatedItems}
                categories={CATEGORIES}
                currentUser={currentUser}
                isLoading={isLoading}
                onVote={handleVote}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onEditPost={handleEditPost}
                onDeletePost={handleDeletePost}
                onRequireAuth={() => openAuth("login")}
                globalQuoteLanguage={langParam}
                loadError={feedLoadError}
                offline={feedOffline}
                onRetry={retryFeed}
              />
            </Suspense>

            {!isLoading && items.length > 0 && (
              <Pagination
                currentPage={visiblePage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isLoading}
              />
            )}
          </div>
        </main>
      </div>

      {!isNativeApp && (
        <FloatingActionButton
          onClick={() => setCreateOpen(true)}
          currentUser={currentUser}
        />
      )}

      <footer className={isNativeApp ? "hidden" : "mt-16 text-center"}>
        <div className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs bg-white dark:bg-slate-800 px-4 py-2 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
          <span>© {new Date().getFullYear()} The Deepam</span>
          <span>•</span>
          <span>Built for growth-minded individuals</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            Made with <Star className="w-3 h-3 text-yellow-500 fill-current" />
          </span>
          <span>•</span>
          <Link to="/privacy" className="hover:underline">Privacy</Link>
          <span>•</span>
          <Link to="/terms" className="hover:underline">Terms</Link>
        </div>
      </footer>
    </div>
  );

  return (
    <>{/* Listen feature disabled: was <PodcastPlayerProvider> */}
    <div className="min-h-screen bg-[hsl(var(--background))] transition-colors">

      <DeleteAccountDialog
        open={deleteAccountOpen}
        onOpenChange={setDeleteAccountOpen}
        onDeleted={() => {
          handleLogout({ showSuccess: false });
          showSuccessToast("Your account has been deleted.");
          navigate("/", { replace: true });
        }}
      />

      <LoginPromptPopup
        open={isLoginPromptVisible}
        onClose={() => handleCloseLoginPrompt({ allowReopen: true })}
        onLogin={handleLoginPromptContinue}
        autoCloseSecondsLeft={autoCloseSecondsLeft}
      />

      <Toaster position="top-right" />

      <AuthModal
        open={authOpen}
        mode={authMode}
        onClose={() => setAuthOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      <CreateContentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateContent}
        currentUser={currentUser}
        onRequireAuth={() => openAuth("login")}
      />

      <Routes>
        <Route index element={FeedLayout()} />
        <Route path="category/:categorySlug" element={FeedLayout()} />
        <Route path="post/:type/:postId" element={<PostDetailPage />} />
        {/* Listen feature disabled - /listen now redirects home via the catch-all below.
        <Route path="listen/*" element={<ListenApp />} /> */}
        <Route path="liked" element={<LikedPage />} />
        <Route path="quotes/authors" element={<AuthorCloudPage />} />
        <Route path="tag-cloud" element={<TagCloudPage />} />
        <Route path="privacy" element={<LegalPage kind="privacy" />} />
        <Route path="terms" element={<LegalPage kind="terms" />} />
        <Route path="ai-prompts" element={<AiPromptsPage />} />
        <Route path="ai-topics" element={<AiTopicsPage />} />
        <Route path="ai-topics/:topicId" element={<AiTopicDetailPage />} />
        <Route path="quotes/author/:slug" element={<AuthorQuotesPage />} />
        <Route path="tags/:slug" element={<TagPage />} />
        <Route path="user/:username/:userId" element={<UserProfilePage />} />
        <Route path="settings/notifications" element={<NotificationSettingsPage />} />
        {/* <Route path="admin/listen/*" element={<AdminListenDashboard />} /> */}
        {/* Unknown paths go to the home feed with a real "/" URL, instead of
            showing the feed under a URL that doesn't describe it. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Listen feature disabled: <GlobalPlayerBar /> */}

      {isNativeApp && <MobileTabBar currentUser={currentUser} />}

      <AnimatePresence>
        {/* The app's tab bar already has a Log in tab, so this floating pill
            would only cover the feed there. */}
        {showLoginPromptBadge && !currentUser?.id && !isNativeApp && (
          <motion.button
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3 }}
            onClick={handleReopenLoginPrompt}
            className="fixed bottom-[calc(1.5rem+var(--tab-bar-height,0px))] left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-semibold text-slate-700 shadow-xl shadow-slate-900/30 backdrop-blur-xl hover:scale-105 transition-transform duration-200"
          >
            <Sparkles className="w-4 h-4 text-amber-500" />
            Reopen login
          </motion.button>
        )}
      </AnimatePresence>
    </div>
    </>
  );
}

export default function App() {


  useGoogleAnalytics();

  useEffect(() => {
    initNativeShell();
    initPushNotifications().catch((error) => console.warn("Push notifications setup failed:", error));
  }, []);

  const routes = (
    <Routes>
      <Route path="/*" element={<AppContent />} />
    </Routes>
  );

  return (
    <ThemeProvider>
      {/* The app signs in natively (GoogleSignInButton), so it skips loading
          Google Identity Services into the WebView. */}
      {isNativeApp ? routes : <GoogleOAuthProvider clientId={CLIENT_ID}>{routes}</GoogleOAuthProvider>}
    </ThemeProvider>
  );
}
